import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Checkbox,
  Chip,
  TextField,

  Autocomplete,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DollarSpinner } from '../components/DollarSpinner';
import { computeRunningBalances, formatCurrency, parseAmountInput } from '../utils/decimal';
import { getLocalDateString } from '../utils/timezone';
import {
  isValidDateStamp,
  INVALID_DATE_MESSAGE,
  DATE_STAMP_MIN,
  DATE_STAMP_MAX
} from '../utils/dateStamp';
import { useTimezoneAwareDateFormatting } from '../components/TimezoneAwareDateDisplay';

import {
  IconPencil as EditIcon,
  IconTrash as DeleteIcon,
  IconDeviceFloppy as SaveIcon,
  IconArrowsSplit,
  IconX as CancelIcon,
  IconDotsVertical as MoreVertIcon,
  IconArchiveOff as UnarchiveIcon,
  IconArrowsLeftRight as SwapHorizIcon,
  IconCheck,
  IconPlus,
  IconChevronDown
} from '@tabler/icons-react';
import api from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useCanEdit, useIsOwner } from '../components/RoleGate.jsx';

import payeeCacheService from '../services/payeeCache';
import { getCachedList, setCachedList, BUDGETS_LIST_CACHE_KEY } from '../utils/listCacheUtil';
import { makeBudgetAcronymOf, withAcronym } from '../utils/budgetGroups';
import { MaskedCurrency, MaskedNumber } from '../components/MaskedValue';
import AmountDisplay, { resolveAmountColor } from '../components/AmountDisplay';
import AmountInputCell, { numberFieldSx } from '../components/AmountInputCell';
import { requiredOutlineSx } from '../utils/requiredFieldSx';
import { useUiPrefs } from '../contexts/UiPrefsContext';
import { useToast } from '../components/ToastNotification';
import { PageHero, ActionBar, Sheet, EntryTypeControl, EntryTypeTrigger, Badge } from '../components/ds';
import PayeeAutocomplete from '../components/PayeeAutocomplete';
import { interpolate, isEntryType, DEFAULT_ENTRY_TYPE, PAYCHECK_SUBTYPE, payeeOptionValue } from '../utils/entryType';
import { payeeOptions } from '../utils/payeeOptions';
import { calculateLastBillPaid, checkingLedgerNames } from '../utils/ledgerGroups';
import { useEntryTypePrompt, entryTypeForSave } from '../hooks/useEntryTypePrompt';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { iconBoxColors } from '../components/ds/iconCategory';
import { ledgerTypeIcon, ledgerTypeCategory } from '../utils/ledgerIcon';
import { CenteredLoader } from '../components/LoadingIndicator';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { usePinnedLine } from '../hooks/usePinnedLine';
import { useOverflowCardSwitch } from '../hooks/useOverflowCardSwitch';
import { notifyTangibleRefresh } from '../utils/tangibleRefresh';
import { reconcileBalance as reconcileBalanceUntilMoved } from '../utils/balanceReconcile';
// Legacy timezone utilities removed - using direct date_stamp now
// Removed formatLegacyDate import - using direct date formatting now

// #1466: unpin the Payee/Budget suggestion popups from the narrow input/column
// width so short option names sit on one line. `width: fit-content` sizes the
// popper to its content; maxWidth 360 caps it so only excessively long names wrap.
const SUGGESTION_POPPER_SLOTPROPS = {
  popper: { style: { width: 'fit-content' }, sx: { maxWidth: 360 } },
  paper: { sx: { minWidth: 160, maxWidth: 360 } },
};

// Memo is a short check-number-style note (#1549): letters, numbers, spaces,
// dashes only, max 24 chars. Strip-then-cap, mirroring the server sanitizer
// in createLine.js/updateLine.js - keep the regex identical on both sides.
// 36 since #2267: a split's parts carry a 13-character stamp ahead of the note.
const MEMO_MAX_LENGTH = 36;
const sanitizeMemo = (v) => v.replace(/[^A-Za-z0-9 -]/g, '').slice(0, MEMO_MAX_LENGTH);

// Reusable action menu component (vertical ellipsis → Edit/Delete)
const ActionMenu = memo(({ onEdit, onDelete, onMoveLedger, moveDisabled = false, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  return (
    <>
      <IconButton size="small" data-tour-id="ledger-line-menu" onClick={(e) => setAnchorEl(e.currentTarget)} disabled={disabled}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { setAnchorEl(null); onEdit(); }}>
          <ListItemIcon><EditIcon size={20} /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {onMoveLedger && (
          <MenuItem
            disabled={moveDisabled}
            onClick={() => { setAnchorEl(null); onMoveLedger(); }}
          >
            <ListItemIcon><SwapHorizIcon size={20} /></ListItemIcon>
            <ListItemText>Move to ledger</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setAnchorEl(null); onDelete(); }}>
          <ListItemIcon><Box component="span" sx={{ color: 'error.main', display: 'inline-flex' }}><DeleteIcon size={20} /></Box></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
});

// v4 live reconcile circle (#1377, V4_ADDENDUM convention 3): a custom-painted
// checkbox so role/keyboard semantics survive. Unreconciled = 1.5px strong
// ring; reconciled = positive tint fill + check. 16px in the desktop table,
// 20px in mobile rows.
const ReconcileCircle = ({ size = 16, ...props }) => {
  const { palette } = useTheme();
  return (
    <Checkbox
      disableRipple
      icon={
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            border: `1.5px solid ${palette.borderStrong}`,
            boxSizing: 'border-box',
          }}
        />
      }
      checkedIcon={
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: palette.tint.positive,
            color: palette.success.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconCheck size={Math.round(size * 0.62)} strokeWidth={3} aria-hidden />
        </Box>
      }
      sx={{ padding: '4px' }}
      {...props}
    />
  );
};

// #1479: a ledger line's assigned budget name links to that budget, styled like the
// PageHero back link (palette.interactive). budgetId is projected on the
// ledger-date-index GSI, so it is available on the client. Mirror of #1477.
const BudgetLink = ({ budgetId, name }) => {
  const navigate = useNavigate();
  const { palette } = useTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(`/budgets/${budgetId}`)}
      sx={{
        maxWidth: '100%',
        background: 'none',
        border: 0,
        padding: 0,
        margin: 0,
        font: 'inherit',
        color: palette.interactive,
        cursor: 'pointer',
        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {name}
    </Box>
  );
};

// Memoized table row. Non-editing rows receive ONLY stable props (#1790): the
// line, its running balance, per-row booleans, and useCallback handlers. The
// edit-only props below (editData through showEditPayeeTooltip) arrive on the
// editing row alone; several of them change identity per keystroke and would
// otherwise defeat memo on every row.
const LedgerTableRow = memo(React.forwardRef(({
  line,
  runningBalance,
  isEditing,
  editData,
  setEditData,
  payeeInputValue,
  handlePayeeInputChange,
  handlePayeeChange,
  filteredPayees,
  payees,
  budgets,
  isUpdatingClear = false,
  handleClearToggle,
  handleEditClick,
  handleEditSave,
  handleEditCancel,
  handleDeleteClick,
  handleMoveClick,
  canMoveLedger = false,
  saving,
  handleCreatePayeeFromInput,
  showEditPayeeTooltip,
  entryPrompt,
  formatDate,
  canEdit = true,
  isHighlighted = false
}, ref) => {
  const { palette } = useTheme();
  const t = useEngineStrings();
  // In-place Entry Type control (#1938) opens below the chip that leads the
  // Memo field.
  const [chipAnchor, setChipAnchor] = useState(null);
  const clearedBoolean = line.cleared === true;
  const mobileGrid = !isEditing;
  // #2288: a line saved on the payee's Paycheck option.
  const isPaycheck = line.lineSubtype === PAYCHECK_SUBTYPE;
  return (
    <TableRow
      ref={ref}
      key={line.lineId}
      data-line-id={line.lineId}
      sx={(theme) => ({
        // v4 (#1377): reconciled state is carried by the circle alone - no row
        // shading. Hover paints the board's row-hover surface and reveals the
        // line menu (always visible on touch).
        opacity: isUpdatingClear ? 0.7 : 1,
        height: '44px',
        transition: `background-color ${theme.ds.motion.fast} ${theme.ds.motion.easeOut}`,
        '@media (hover: hover)': {
          '& .br-line-menu': { opacity: 0 },
          '&:hover .br-line-menu': { opacity: 1 },
          '&:hover': { backgroundColor: theme.palette.surface.rowHover },
        },
        ...(isHighlighted && {
          animation: 'highlightFlash 0.35s ease-in-out 2'
        }),
        '@keyframes highlightFlash': {
          '0%, 100%': { backgroundColor: 'inherit' },
          '50%': { backgroundColor: alpha(theme.palette.success.main, 0.3) }
        },
        '& .MuiTableCell-root': {
          padding: '5px 8px',
          fontSize: '13px'
        },
        ...(mobileGrid && {
          [theme.breakpoints.down('sm')]: {
            display: 'grid',
            gridTemplateColumns: '60px 1fr 70px 32px',
            gridTemplateRows: 'auto auto',
            rowGap: theme.spacing(0.5),
            height: 'auto',
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& > .MuiTableCell-root': {
              display: 'flex',
              alignItems: 'center',
              borderBottom: 0,
              fontSize: '0.65rem',
              padding: '2px 4px'
            },
            '& > .MuiTableCell-root:nth-of-type(1)': { gridArea: '2 / 1', justifyContent: 'flex-start' },
            '& > .MuiTableCell-root:nth-of-type(2)': { gridArea: '1 / 1', justifyContent: 'flex-start' },
            '& > .MuiTableCell-root:nth-of-type(3)': { gridArea: '1 / 2', justifyContent: 'flex-start' },
            // (4) memo has no slot in the compact grid (#1549)
            '& > .MuiTableCell-root:nth-of-type(4)': { display: 'none' },
            '& > .MuiTableCell-root:nth-of-type(5)': { gridArea: '2 / 2', justifyContent: 'center' },
            '& > .MuiTableCell-root:nth-of-type(6)': { gridArea: '1 / 3', justifyContent: 'center' },
            '& > .MuiTableCell-root:nth-of-type(7)': { gridArea: '2 / 3 / 3 / 5', justifyContent: 'flex-end' },
            '& > .MuiTableCell-root:nth-of-type(8)': { gridArea: '1 / 4', justifyContent: 'center' }
          }
        })
      })}
    >
      <TableCell>
        <Box display="flex" alignItems="center">
          <ReconcileCircle
            checked={clearedBoolean}
            onChange={() => handleClearToggle(line)}
            disabled={!canEdit || isUpdatingClear || isEditing}
            inputProps={{ 'aria-label': clearedBoolean ? 'Reconciled' : 'Reconcile' }}
          />
          {isUpdatingClear && (
            <Box sx={{ width: 16, height: 16, ml: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSpinner size="small" />
            </Box>
          )}
        </Box>
      </TableCell>

      {/* Date Cell */}
      <TableCell align="left" sx={{ color: palette.text.secondary }}>
        {isEditing ? (
          <TextField
            type="date"
            inputProps={{ min: DATE_STAMP_MIN, max: DATE_STAMP_MAX }}
            value={editData.dateStamp || ''}
            onChange={(e) => setEditData(prev => ({ ...prev, dateStamp: e.target.value }))}
            size="small"
            fullWidth
            sx={{
              '& .MuiInputBase-root': { height: '32px' },
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}
          />
        ) : (
          <>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {formatDate(line.dateStamp, 'date-only')}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              {formatDate(line.dateStamp, 'date-short')}
            </Box>
          </>
        )}
      </TableCell>

      {/* Payee Cell */}
      <TableCell>
        {isEditing ? (
          <>
          <Tooltip
            title="Please select a payee"
            open={showEditPayeeTooltip}
            arrow
            placement="top"
          >
            <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
              <PayeeAutocomplete
                size="small"
                fullWidth
                sx={{ flex: 1, minWidth: 0 }}
                onOpen={entryPrompt?.close}
                prompt={entryPrompt}
                incomeAllowed={entryPrompt?.incomeAllowed !== false}
                options={filteredPayees}
                getOptionLabel={(option) => option.name || ''}
                value={payeeOptionValue(payees, editData.payeeId, entryPrompt?.paycheck)}
                onChange={handlePayeeChange}
                inputValue={payeeInputValue}
                onInputChange={handlePayeeInputChange}
                freeSolo
                disablePortal
                slotProps={SUGGESTION_POPPER_SLOTPROPS}
                blurOnSelect
                clearOnBlur={false}
                selectOnFocus={false}
                handleHomeEndKeys={false}
                disableListWrap
                filterOptions={(options) => options}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Type payee name..."
                    required
                    error={!editData.payeeId || editData.payeeId === ''}
                    title={(!editData.payeeId || editData.payeeId === '') ? 'Payee is required' : ''}
                    onBlur={(e) => entryPrompt?.onPayeeBlur(payeeInputValue, e)}
                    sx={{
                      '& .MuiInputBase-root': { height: '32px' },
                      '& .MuiInputBase-input': { fontSize: { xs: '0.65rem', sm: '0.875rem' } },
                      '& .MuiInputBase-input::placeholder': { fontSize: { xs: '0.65rem', sm: '0.875rem' } }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && payeeInputValue.trim() && !payees.find(p => p.name.toLowerCase() === payeeInputValue.toLowerCase())) {
                        handleCreatePayeeFromInput(payeeInputValue.trim());
                      } else if (e.key === 'Enter' && payeeInputValue.trim()) {
                        // Enter commits a typed existing name too (#1938 revision).
                        entryPrompt?.commitText(payeeInputValue);
                      }
                    }}
                  />
                )}
              />
            </div>
          </Tooltip>
          </>
        ) : (
          <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {line.payeeName && line.payeeName.trim() ? line.payeeName : 'Unknown Payee'}
            {isPaycheck ? (
              // #2288: the Paycheck badge after the name, as on the picker option.
              <Badge variant="neutral" data-testid="line-paycheck-badge" sx={{ marginLeft: '8px' }}>{t['payees.paycheckBadge']}</Badge>
            ) : null}
          </Typography>
        )}
      </TableCell>

      {/* Memo Cell (#1549) - short check-number-style note */}
      <TableCell align="left">
        {isEditing ? (
          // The Entry Type chip leads the Memo field (#1938, owner 2026-09-29):
          // the type reads as a note on the line; the control opens below it.
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <EntryTypeTrigger
              ref={setChipAnchor}
              type={entryPrompt?.chip?.type}
              differs={entryPrompt?.chip?.differs}
              readOnly={Boolean(entryPrompt?.chip?.locked)}
              open={Boolean(entryPrompt?.open)}
              onToggle={entryPrompt?.toggle}
            />
            <EntryTypeControl
              placement="float"
              open={Boolean(entryPrompt?.open)}
              anchorEl={chipAnchor}
              value={entryPrompt?.value}
              onChange={entryPrompt?.setValue}
              onClose={entryPrompt?.close}
              prompt={entryPrompt?.entry}
              pending={Boolean(entryPrompt?.pending)}
              onProviderChoice={entryPrompt?.setProviderChoice}
              incomeAllowed={entryPrompt?.incomeAllowed !== false}
            />
            <TextField
              value={editData.memo || ''}
              onChange={(e) => setEditData(prev => ({ ...prev, memo: sanitizeMemo(e.target.value) }))}
              size="small"
              fullWidth
              placeholder="memo"
              inputProps={{ maxLength: MEMO_MAX_LENGTH }}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiInputBase-root': { height: '32px' },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
            />
          </Box>
        ) : (
          // Saved row (#1943): the display-only chip leads the cell at the entry
          // row's width, so the types read as one column; an untyped line keeps
          // the width blank so Memo text stays aligned.
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
            <EntryTypeTrigger readOnly type={line.entryType} />
            {line.memo ? (
              <Typography variant="body2" noWrap sx={{ color: palette.text.secondary, minWidth: 0 }}>
                {line.memo}
              </Typography>
            ) : null}
          </Box>
        )}
      </TableCell>

      {/* Amount Cell (unified debit/credit) */}
      <TableCell align="right">
        {isEditing ? (
          <AmountInputCell
            debitValue={editData.debit}
            creditValue={editData.credit}
            onDebitChange={(val) => setEditData(prev => ({ ...prev, debit: val, credit: val ? '' : prev.credit }))}
            onCreditChange={(val) => setEditData(prev => ({ ...prev, credit: val, debit: val ? '' : prev.debit }))}
            depositOnly={Boolean(entryPrompt?.paycheck)}
            depositOnlyTitle={t['entryType.depositOnly']}
          />
        ) : (
          <AmountDisplay amountCents={line.amountCents} variant="row" />
        )}
      </TableCell>

      {/* Budget Cell */}
      <TableCell align="left">
        {isEditing ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <EnhancedBudgetAutocomplete
              budgets={budgets}
              value={budgets.find(b => b.budgetId === editData.budgetId) || null}
              onChange={(event, newValue) => {
                setEditData(prev => ({ ...prev, budgetId: newValue ? newValue.budgetId : '0' }));
              }}
              onTabSelect={() => {
                const saveButton = document.querySelector(`[data-testid="save-button-${line.lineId}"]`);
                saveButton?.focus();
              }}
            />
            <IconButton
              size="small"
              onClick={() => handleEditSave(line)}
              disabled={saving || Boolean(entryPrompt?.pending)}
              data-testid={`save-button-${line.lineId}`}
              data-entry-type-save="true"
              sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
            >
              {saving ? <DollarSpinner size="small" /> : <SaveIcon />}
            </IconButton>
            <IconButton size="small" onClick={handleEditCancel} disabled={saving} sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}>
              <CancelIcon />
            </IconButton>
          </Box>
        ) : (
          line.budgetId ? (
            <BudgetLink budgetId={line.budgetId} name={line.budgetName || `Budget ${line.budgetId}`} />
          ) : '-'
        )}
      </TableCell>

      {/* Balance Cell - running subtotal, colored per DESIGN_SYSTEM section 11 */}
      <TableCell align="right">
        <Typography component="span" fontWeight="medium">
          <MaskedCurrency
            value={runningBalance}
            style={{
              color: resolveAmountColor(
                palette,
                'subtotal',
                runningBalance < 0,
                runningBalance === 0
              ),
            }}
          />
        </Typography>
      </TableCell>

      {/* Actions Cell - editor/owner only; v4 reveals on row hover */}
      <TableCell align="center">
        {!isEditing && canEdit && (
          <Box className="br-line-menu" sx={{ display: 'inline-flex' }}>
            <ActionMenu
              onEdit={() => handleEditClick(line)}
              onDelete={() => handleDeleteClick(line)}
              onMoveLedger={() => handleMoveClick(line)}
              moveDisabled={!canMoveLedger}
            />
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}));

// Enhanced Budget Autocomplete Component with keyboard navigation
const EnhancedBudgetAutocomplete = memo(({
  budgets,
  value,
  onChange,
  onTabSelect,
  // #1520: optional parent-controlled input text (payee-parity). When the
  // parent passes inputValue/onInputChange it owns the display text and can
  // reset it after save; otherwise the internal state fallback applies
  // (edit rows, which unmount on save and never ghost).
  inputValue: inputValueProp,
  onInputChange: onInputChangeProp,
  inputProps = {},
  size = "small",
  fullWidth = true,
  // The desktop add-row passes the short "Budget" (#1959); edit rows and
  // cards keep the longer hint.
  placeholder = "Type to filter budgets..."
}) => {
  const [internalInputValue, setInternalInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const inputValue = inputValueProp !== undefined ? inputValueProp : internalInputValue;
  
  // Filter options based on input - no 'No Budget' option, just actual budgets
  const filteredOptions = useMemo(() => {
    if (!inputValue) return budgets; // Show all budgets by default
    return budgets.filter(option =>
      option.name.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [budgets, inputValue]);

  // Auto-highlight single option
  useEffect(() => {
    if (filteredOptions.length === 1 && inputValue.trim()) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [filteredOptions, inputValue]);

  const handleKeyDown = (event) => {
    if (event.key === 'Tab' && highlightedIndex >= 0) {
      event.preventDefault();
      const selectedOption = filteredOptions[highlightedIndex];
      onChange(event, selectedOption);
      if (onTabSelect) {
        onTabSelect();
      }
    } else if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault();
      const selectedOption = filteredOptions[highlightedIndex];
      onChange(event, selectedOption);
      if (onTabSelect) {
        onTabSelect();
      }
    } else if (event.key === ' ' && highlightedIndex >= 0) {
      event.preventDefault();
      const selectedOption = filteredOptions[highlightedIndex];
      onChange(event, selectedOption);
      if (onTabSelect) {
        onTabSelect();
      }
    }
  };

  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      options={filteredOptions}
      getOptionLabel={(option) => option.name || ''}
      value={value}
      onChange={onChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        if (onInputChangeProp) {
          onInputChangeProp(event, newInputValue);
        } else {
          setInternalInputValue(newInputValue);
        }
      }}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      renderInput={(params) => (
        <TextField
          {...params}
          {...inputProps}
          variant="outlined"
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          sx={{
            // #1425 / convention 12: on xs the selector matches the Payee
            // field (default small Autocomplete: 16px font, natural height)
            // so the row reads as one pair and focusing never zooms iOS.
            // Desktop rows keep the dense 32px / 0.875rem table look.
            '& .MuiInputBase-root': { height: { xs: 'auto', sm: '32px' } },
            '& .MuiInputBase-input': { fontSize: { xs: '1rem', sm: '0.875rem' } },
            '& .MuiInputBase-input::placeholder': { fontSize: { xs: '1rem', sm: '0.875rem' } },
            ...inputProps.sx
          }}
        />
      )}
      filterOptions={(options) => options} // We handle filtering manually
      slotProps={SUGGESTION_POPPER_SLOTPROPS}
      openOnFocus
      clearOnBlur={false}
      selectOnFocus
      ListboxProps={{
        style: {
          maxHeight: 200
        }
      }}
      renderOption={(props, option) => {
        // Budgets key on budgetId; MUI supplies its own key in props, so
        // pull it out before spreading and key on the real id (#1558).
        const { key: _key, ...optionProps } = props;
        return (
          <li key={option.budgetId} {...optionProps}>
            {withAcronym(option.name, option.categoryAcronym)}
          </li>
        );
      }}
    />
  );
});

// Split pill (#2263): icon only, right of the budget picker on the add row.
// Closed: outline in the interactive colour, faded fill, icon in the border
// colour. Open: solid interactive fill, icon in the on-interactive colour.
const SplitPill = ({ open, onToggle }) => {
  const { palette, ds } = useTheme();
  return (
    <Box
      component="button"
      type="button"
      aria-label="Split"
      aria-pressed={open}
      data-testid="ledger-new-split"
      onClick={onToggle}
      sx={{
        flex: '0 0 auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        padding: 0,
        cursor: 'pointer',
        borderRadius: `${ds.radius.button}px`,
        border: `1px solid ${palette.interactive}`,
        backgroundColor: open ? palette.interactive : palette.tint.interactive,
        color: open ? palette.interactiveOn : palette.interactive,
        transition: `background-color ${ds.motion.fast} ${ds.motion.easeOut}, color ${ds.motion.fast} ${ds.motion.easeOut}`,
        '&:focus-visible': { outline: `2px solid ${palette.interactive}`, outlineOffset: 1 },
      }}
    >
      <IconArrowsSplit size={18} stroke={2} aria-hidden />
    </Box>
  );
};

// The last part of a split (#2263): the total less every typed part, shown
// read-only in the debit or credit column the total sits in.
const DerivedAmount = ({ cents }) => {
  const text = cents === 0 ? '' : (Math.abs(cents) / 100).toFixed(2);
  const field = (placeholder, value) => (
    <TextField
      size="small"
      value={value}
      placeholder={placeholder}
      inputProps={{ readOnly: true, 'aria-label': `Part amount ${placeholder}`, tabIndex: -1 }}
      sx={numberFieldSx}
    />
  );
  return (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
      {field('debit', cents < 0 ? text : '')}
      {field('credit', cents > 0 ? text : '')}
    </Box>
  );
};

// The split drawer (#2263): one table row per part under the add row, so each
// part's amount and budget sit under the add row's own columns. The drawer
// reaches from the Part label (the memo column) to the budget column. The
// last part's amount is derived; a later "+ add split" is one more part in
// `parts`, nothing else.
const SplitPartRows = ({ parts, lastCents, budgets, onPartChange }) => {
  const { palette } = useTheme();
  const edge = `1px solid ${palette.divider}`;
  return parts.map((part, index) => {
    const first = index === 0;
    const last = index === parts.length - 1;
    const frame = { borderTop: first ? edge : 'none', borderBottom: last ? edge : 'none' };
    return (
      <TableRow
        key={index}
        data-testid={`ledger-split-part-${index + 1}`}
        sx={{
          backgroundColor: palette.surface.recessed,
          '& .MuiTableCell-root': { padding: '4px 8px', borderBottom: 'none', fontSize: '0.875rem' },
        }}
      >
        <TableCell colSpan={3} />
        <TableCell align="right" sx={{ ...frame, borderLeft: edge, color: palette.text.dim, whiteSpace: 'nowrap' }}>
          Part {index + 1}
        </TableCell>
        <TableCell align="center" sx={frame}>
          {last ? (
            <DerivedAmount cents={lastCents} />
          ) : (
            <AmountInputCell
              debitValue={part.debit}
              creditValue={part.credit}
              // AmountInputCell clears the other side itself; each handler sets
              // its own field only, so the two calls never overwrite each other.
              onDebitChange={(val) => onPartChange(index, { debit: val })}
              onCreditChange={(val) => onPartChange(index, { credit: val })}
            />
          )}
        </TableCell>
        <TableCell align="center" colSpan={2} sx={{ ...frame, borderRight: edge }}>
          <EnhancedBudgetAutocomplete
            budgets={budgets}
            value={budgets.find((b) => b.budgetId === part.budgetId) || null}
            onChange={(event, newValue) => onPartChange(index, { budgetId: newValue ? newValue.budgetId : '0' })}
            inputValue={part.input}
            onInputChange={(event, value) => onPartChange(index, { input: value })}
            inputProps={{ 'aria-label': `Part ${index + 1} budget` }}
            placeholder="Budget"
          />
        </TableCell>
        <TableCell />
      </TableRow>
    );
  });
};

// New row component for adding transactions
const NewLedgerRow = memo(({
  newRowData,
  setNewRowData,
  newRowPayeeInput,
  handleNewRowPayeeInputChange,
  newRowBudgetInput,
  handleNewRowBudgetInputChange,
  handleNewRowPayeeChange,
  filteredPayees,
  payees,
  budgets,
  handleSaveNewRow,
  savingNewRow,
  handleCreatePayeeFromNewRow,
  showNewRowPayeeTooltip,
  entryPrompt,
  dateFieldRef,
  payeeFieldRef,
  debitFieldRef,
  creditFieldRef,
  budgetFieldRef,
  saveButtonRef,
  splitOpen = false,
  onSplitToggle
}) => {
  // 'UI Tips' OFF (#1282) silences the indigo required-empty payee outline here,
  // matching the amount cells; the standard focus ring is untouched.
  const { uiTips } = useUiPrefs();
  const t = useEngineStrings();
  // The Split pill is a desktop control (#2263): md and up only.
  const isMdUp = useMediaQuery((theme) => theme.breakpoints.up('md'));
  // In-place Entry Type control (#1938) opens below the chip that leads the
  // Memo field.
  const [chipAnchor, setChipAnchor] = useState(null);
  const shortDate = newRowData.dateStamp && newRowData.dateStamp.length >= 10
    ? `${newRowData.dateStamp.slice(5, 7)}/${newRowData.dateStamp.slice(8, 10)}/${newRowData.dateStamp.slice(2, 4)}`
    : 'Pick';
  return (
    <TableRow sx={(theme) => ({
      // v4 (#1377, convention 4): the inline quick-add row reads as the
      // board's recessed strip, hairline-bounded (the 2px primary underline is
      // retired).
      backgroundColor: theme.palette.surface.recessed,
      height: '48px',
      '& > *': { borderBottom: `1px solid ${theme.palette.divider}` },
      '& .MuiTableCell-root': {
        padding: '6px 8px',
        fontSize: '0.875rem'
      },
      [theme.breakpoints.down('sm')]: {
        display: 'grid',
        gridTemplateColumns: '52px 1fr 80px 36px',
        gridTemplateRows: 'auto auto',
        rowGap: theme.spacing(0.5),
        height: 'auto',
        py: 0.75,
        '& > .MuiTableCell-root': {
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.65rem',
          padding: '2px 4px'
        },
        '& > .MuiTableCell-root:nth-of-type(1)': { gridArea: '2 / 1', justifyContent: 'flex-start' },
        '& > .MuiTableCell-root:nth-of-type(2)': { gridArea: '1 / 1', justifyContent: 'flex-start' },
        '& > .MuiTableCell-root:nth-of-type(3)': { gridArea: '1 / 2 / 2 / 5' },
        // (4) memo has no slot in the compact grid (#1549)
        '& > .MuiTableCell-root:nth-of-type(4)': { display: 'none' },
        '& > .MuiTableCell-root:nth-of-type(5)': { gridArea: '2 / 2', justifyContent: 'center' },
        '& > .MuiTableCell-root:nth-of-type(6)': { gridArea: '2 / 3 / 3 / 5', justifyContent: 'center' },
        '& > .MuiTableCell-root:nth-of-type(7)': { display: 'none' },
        '& > .MuiTableCell-root:nth-of-type(8)': { display: 'none' }
      }
    })}>
      <TableCell>
        {/* Live reconcile circle - lets user create a line already-reconciled */}
        <Box display="flex" alignItems="center">
          <ReconcileCircle
            checked={newRowData.cleared === true}
            onChange={(e) => setNewRowData(prev => ({ ...prev, cleared: e.target.checked }))}
            inputProps={{ 'aria-label': 'Reconcile new line' }}
          />
        </Box>
      </TableCell>

      {/* Date Cell */}
      <TableCell data-tour-id="ledger-new-date">
        <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '100%' }}>
          <TextField
            inputRef={dateFieldRef}
            type="date"
            inputProps={{ min: DATE_STAMP_MIN, max: DATE_STAMP_MAX }}
            value={newRowData.dateStamp || ''}
            onChange={(e) => setNewRowData(prev => ({ ...prev, dateStamp: e.target.value }))}
            size="small"
            fullWidth
            sx={{
              '& .MuiInputBase-root': { height: '32px' },
              '& .MuiInputBase-input': { fontSize: '0.875rem' }
            }}
          />
        </Box>
        <Box sx={{
          display: { xs: 'inline-flex', sm: 'none' },
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          height: 28,
          minWidth: 48,
          px: 0.5,
          fontSize: '0.65rem',
          cursor: 'pointer'
        }}>
          <span>{shortDate}</span>
          <Box component="input"
            type="date"
            min={DATE_STAMP_MIN}
            max={DATE_STAMP_MAX}
            value={newRowData.dateStamp || ''}
            onChange={(e) => setNewRowData(prev => ({ ...prev, dateStamp: e.target.value }))}
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              border: 0,
              padding: 0,
              background: 'transparent',
              color: 'transparent'
            }}
          />
        </Box>
      </TableCell>

      {/* Payee Cell */}
      <TableCell data-tour-id="ledger-new-payee">
        <Tooltip
          title="Please select a payee"
          open={showNewRowPayeeTooltip}
          arrow
          placement="top"
        >
          <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <PayeeAutocomplete
              size="small"
              fullWidth
              sx={{ flex: 1, minWidth: 0 }}
              onOpen={entryPrompt?.close}
              prompt={entryPrompt}
              incomeAllowed={entryPrompt?.incomeAllowed !== false}
              options={filteredPayees}
              getOptionLabel={(option) => option.name || ''}
              value={payeeOptionValue(payees, newRowData.payeeId, entryPrompt?.paycheck)}
              onChange={handleNewRowPayeeChange}
              inputValue={newRowPayeeInput}
              onInputChange={handleNewRowPayeeInputChange}
              freeSolo
              disablePortal
              slotProps={SUGGESTION_POPPER_SLOTPROPS}
              blurOnSelect
              clearOnBlur={false}
              selectOnFocus={false}
              handleHomeEndKeys={false}
              disableListWrap
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={payeeFieldRef}
                  placeholder="Payee"
                  required
                  title={(!newRowData.payeeId || newRowData.payeeId === '') ? 'Payee is required' : ''}
                  onBlur={(e) => entryPrompt?.onPayeeBlur(newRowPayeeInput, e)}
                  sx={(theme) => ({
                    '& .MuiInputBase-root': { height: '32px' },
                    '& .MuiInputBase-input': { fontSize: { xs: '0.65rem', sm: '0.875rem' } },
                    '& .MuiInputBase-input::placeholder': { fontSize: { xs: '0.65rem', sm: '0.875rem' } },
                    // Required prompt = indigo accent (#1210), not error-red.
                    // Gated by 'UI Tips' (#1282): off = no indigo prompt.
                    ...((!newRowData.payeeId || newRowData.payeeId === '') && uiTips ? requiredOutlineSx(theme) : {})
                  })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newRowPayeeInput.trim() && !payees.find(p => p.name.toLowerCase() === newRowPayeeInput.toLowerCase())) {
                      handleCreatePayeeFromNewRow(newRowPayeeInput.trim());
                    } else if (e.key === 'Enter' && newRowPayeeInput.trim()) {
                      // Enter commits a typed existing name too (#1938 revision).
                      entryPrompt?.commitText(newRowPayeeInput);
                    }
                  }}
                />
              )}
            />
          </div>
        </Tooltip>
      </TableCell>

      {/* Memo Cell (#1549). The Entry Type chip leads it (#1938, owner
          2026-09-29): the type reads as a note on the line; the control opens
          below the chip. */}
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <EntryTypeTrigger
            ref={setChipAnchor}
            type={entryPrompt?.chip?.type}
            differs={entryPrompt?.chip?.differs}
            readOnly={Boolean(entryPrompt?.chip?.locked)}
            open={Boolean(entryPrompt?.open)}
            onToggle={entryPrompt?.toggle}
          />
          <EntryTypeControl
            placement="float"
            open={Boolean(entryPrompt?.open)}
            anchorEl={chipAnchor}
            value={entryPrompt?.value}
            onChange={entryPrompt?.setValue}
            onClose={entryPrompt?.close}
            prompt={entryPrompt?.entry}
            pending={Boolean(entryPrompt?.pending)}
            onProviderChoice={entryPrompt?.setProviderChoice}
            incomeAllowed={entryPrompt?.incomeAllowed !== false}
          />
          <TextField
            value={newRowData.memo || ''}
            onChange={(e) => setNewRowData(prev => ({ ...prev, memo: sanitizeMemo(e.target.value) }))}
            size="small"
            fullWidth
            placeholder="memo"
            inputProps={{ maxLength: MEMO_MAX_LENGTH }}
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiInputBase-root': { height: '32px' },
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}
          />
        </Box>
      </TableCell>

      {/* Amount Cell (unified debit/credit) */}
      <TableCell align="center" data-tour-id="ledger-new-amount">
        <AmountInputCell
          debitValue={newRowData.debit}
          creditValue={newRowData.credit}
          onDebitChange={(val) => setNewRowData(prev => ({ ...prev, debit: val, credit: val ? '' : prev.credit }))}
          onCreditChange={(val) => setNewRowData(prev => ({ ...prev, credit: val, debit: val ? '' : prev.debit }))}
          debitRef={debitFieldRef}
          creditRef={creditFieldRef}
          depositOnly={Boolean(entryPrompt?.paycheck)}
          depositOnlyTitle={t['entryType.depositOnly']}
        />
      </TableCell>

      {/* Budget Cell. Spans the Balance column, which a new row leaves empty,
          so full budget names fit (#1959). */}
      <TableCell align="center" colSpan={2} data-tour-id="ledger-new-budget">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Split open (#2263): the budgets live in the drawer, so a grey
              Splitting box stands where the picker was, same width. */}
          {splitOpen ? (
            <Box
              data-testid="ledger-new-splitting"
              sx={(theme) => ({
                flex: 1,
                minWidth: 0,
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: `${theme.ds.radius.button}px`,
                color: theme.palette.text.dim,
                fontSize: '0.875rem',
              })}
            >
              Splitting
            </Box>
          ) : (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <EnhancedBudgetAutocomplete
                budgets={budgets}
                value={budgets.find(b => b.budgetId === newRowData.budgetId) || null}
                onChange={(event, newValue) => {
                  setNewRowData(prev => ({ ...prev, budgetId: newValue ? newValue.budgetId : '0' }));
                }}
                inputValue={newRowBudgetInput}
                onInputChange={handleNewRowBudgetInputChange}
                onTabSelect={() => {
                  saveButtonRef.current?.focus();
                }}
                inputProps={{
                  ref: budgetFieldRef
                }}
                placeholder="Budget"
              />
            </Box>
          )}
          {isMdUp && onSplitToggle && <SplitPill open={splitOpen} onToggle={onSplitToggle} />}
        </Box>
      </TableCell>

      {/* Actions Cell: the save button sits at the far right, where saved
          rows carry their menu (#1959). */}
      <TableCell align="center">
        <IconButton
          ref={saveButtonRef}
          size="small"
          data-tour-id="ledger-new-save"
          data-entry-type-save="true"
          onClick={handleSaveNewRow}
          disabled={savingNewRow || Boolean(entryPrompt?.pending)}
          color="primary"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSaveNewRow();
            }
          }}
        >
          {savingNewRow ? <DollarSpinner size="small" /> : <SaveIcon />}
        </IconButton>
      </TableCell>
    </TableRow>
  );
});

// v4 mobile card for one NON-editing line (#1790). Memoized on stable props
// only (the parent passes useCallback handlers and per-row booleans), so a
// keystroke in the quick-add Sheet or in the editing card re-renders the page
// shell, not every loaded card. The editing card stays inline in the page.
const LedgerLineCard = memo(React.forwardRef(({
  line,
  isUpdatingClear = false,
  canEdit = true,
  showMenu = false,
  menuDisabled = false,
  moveDisabled = false,
  onClearToggle,
  onEdit,
  onDelete,
  onMove
}, ref) => {
  const { palette } = useTheme();
  const t = useEngineStrings();
  const cleared = line.cleared === true;
  return (
    <Box
      ref={ref}
      data-line-id={line.lineId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: '9px 0',
        borderBottom: `1px solid ${palette.divider}`,
        '&:last-child': { borderBottom: 'none' },
        opacity: isUpdatingClear ? 0.7 : 1,
      }}
    >
      <ReconcileCircle
        size={20}
        checked={cleared}
        onChange={() => onClearToggle(line)}
        disabled={!canEdit || isUpdatingClear}
        inputProps={{ 'aria-label': cleared ? 'Reconciled' : 'Reconcile' }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '14px', color: palette.text.default }}>
          {line.payeeName && line.payeeName.trim() ? line.payeeName : 'Unknown Payee'}
          {line.lineSubtype === PAYCHECK_SUBTYPE ? (
            // #2288: the Paycheck badge after the name.
            <Badge variant="neutral" data-testid="line-paycheck-badge" sx={{ marginLeft: '8px' }}>{t['payees.paycheckBadge']}</Badge>
          ) : null}
        </Typography>
        <Typography noWrap component="div" sx={{ fontSize: '12px', color: palette.text.dim, mt: '1px' }}>
          {line.budgetId ? <BudgetLink budgetId={line.budgetId} name={line.budgetName || `Budget ${line.budgetId}`} /> : '-'}
        </Typography>
        {/* The memo line (#1943, owner 2026-09-29): the chip sits left of the
            memo; a typed line with no memo shows the chip alone; an untyped
            line without a memo shows nothing here. */}
        {line.memo || isEntryType(line.entryType) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px', minWidth: 0 }}>
            <EntryTypeTrigger readOnly type={line.entryType} />
            {line.memo ? (
              <Typography noWrap sx={{ fontSize: '12px', color: palette.text.dim, minWidth: 0 }}>
                {line.memo}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <AmountDisplay amountCents={line.amountCents} variant="row" />
      </Box>
      {showMenu && (
        <ActionMenu
          onEdit={() => onEdit(line)}
          onDelete={() => onDelete(line)}
          onMoveLedger={() => onMove(line)}
          moveDisabled={moveDisabled}
          disabled={menuDisabled}
        />
      )}
    </Box>
  );
}));
LedgerLineCard.displayName = 'LedgerLineCard';

const LedgerDetail = ({ isAuthReady = false }) => {
  const { id } = useParams();
  const { activeGroupId } = useGroup();
  const canEdit = useCanEdit();
  const isOwner = useIsOwner();
  const navigate = useNavigate();
  const theme = useTheme();
  const { palette, ds } = theme;
  // v4 (#1377): xs renders the board's date-grouped cards + quick-add Sheet
  // (convention 4); sm+ keeps the table with the inline quick-add row.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // #1465: also drop to cards when the desktop table would ACTUALLY overflow
  // (narrow window / zoom), detected off the live table with a stable probe for the
  // non-oscillating restore. useCardView replaces isMobile wherever the choice is
  // "show cards"; tableContainerRef is the desktop table's overflow-x container.
  const tableContainerRef = useRef(null);
  const [tableOverflows, overflowProbeRef] = useOverflowCardSwitch(tableContainerRef);
  const useCardView = isMobile || tableOverflows;
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  // #1577: quick-add Sheet validation feedback - bottom-anchored error toasts
  // float just above the open sheet (the desktop payee tooltip and the
  // full-page error Alert are both invisible under it).
  const { showError, showSuccess } = useToast();
  const engineStrings = useEngineStrings();
  const sheetContentRef = useRef(null);
  const [unarchiving, setUnarchiving] = useState(false);
  
  // Use the proper timezone-aware date formatting hook
  const { formatDate } = useTimezoneAwareDateFormatting();
  
  const [ledger, setLedger] = useState(null);
  const [balance, setBalance] = useState(null);
  // Mirror so write handlers can snapshot the pre-write balance without
  // threading `balance` through their dependency arrays.
  const balanceRef = useRef(null);
  balanceRef.current = balance;
  const [initialLoading, setInitialLoading] = useState(true);

  // Reconcile the displayed balance with the server after a line write: the
  // balance read is eventually consistent (stream-delta cache, #857), so a
  // naive immediate refetch can overwrite the correct optimistic value with
  // the stale pre-write one (#1046). Polling logic in utils/balanceReconcile.
  // A reconcile toggle watches clearedCents instead of the balance (#2070).
  const reconcileBalance = useCallback((preWriteCents, deltaCents, field = 'balanceCents') => {
    reconcileBalanceUntilMoved({
      fetchBalance: () => api.get(activeGroupId, `/ledgers/${encodeURIComponent(id)}/balance`),
      applyBalance: setBalance,
      preWriteCents,
      deltaCents,
      field,
    });
  }, [activeGroupId, id]);
  // Optimistic balance bump for the page's own writes (#2070): the balance
  // always moves by balanceDelta; clearedCents moves only when the write
  // touches a reconciled line, and only when the page already holds a number.
  const bumpBalance = useCallback(({ balanceDelta = 0, clearedDelta = 0 }) => {
    setBalance(prev => ({
      ...prev,
      balanceCents: (prev?.balanceCents ?? 0) + balanceDelta,
      ...(clearedDelta !== 0 && typeof prev?.clearedCents === 'number'
        ? { clearedCents: prev.clearedCents + clearedDelta }
        : {}),
    }));
  }, []);
  const [error, setError] = useState(null);
  const [updatingClear, setUpdatingClear] = useState(new Set());
  const [showUnreconciledOnly, setShowUnreconciledOnly] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [editData, setEditData] = useState({});
  const [highlightedLineId, setHighlightedLineId] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [payees, setPayees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [payeeInputValue, setPayeeInputValue] = useState('');

  // "Move to ledger" (#760): list of other ledgers + dialog state.
  const [ledgers, setLedgers] = useState([]);
  // Every ledger, archived too (#1944): the picker's Ledgers group sorts the
  // archived ones last and last-bill-paid matches the Checking ones.
  const [allLedgers, setAllLedgers] = useState([]);
  const [moveLine, setMoveLine] = useState(null);
  const [moveTargetLedger, setMoveTargetLedger] = useState(null);
  const [moving, setMoving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, line: null });

  // Refs for focus management in new row
  const dateFieldRef = useRef(null);
  const payeeFieldRef = useRef(null);
  const debitFieldRef = useRef(null);
  const creditFieldRef = useRef(null);
  const budgetFieldRef = useRef(null);
  const saveButtonRef = useRef(null);

  // Load ledger lines for infinite scroll. Cursor-based (#1292): given the
  // server's opaque nextCursor (undefined for the first page), returns one page
  // of lines plus the cursor for the following page. Lines are sorted desc by
  // dateStamp then lineId to break ties consistently.
  const loadLedgerLinesData = useCallback(async (cursor) => {
    if (!activeGroupId) return { lines: [], nextCursor: null };
    const qs = new URLSearchParams({ ledgerId: String(id), order: 'desc' });
    if (cursor) qs.set('cursor', cursor);
    const response = await api.get(activeGroupId, `/lines?${qs.toString()}`);
    const lines = (response.lines || []).sort((a, b) => {
      if (a.dateStamp !== b.dateStamp) {
        return (b.dateStamp || '').localeCompare(a.dateStamp || '');
      }
      return (b.lineId || '').localeCompare(a.lineId || '');
    });
    return { lines, nextCursor: response.nextCursor || null };
  }, [id, activeGroupId]);

  // Infinite scroll hook
  const {
    data: ledgerLines,
    loading: scrollLoading,
    loadingMore,
    hasMore,
    autoLoadPaused,
    error: scrollError,
    loadInitialData,
    lastElementRef,
    loadMore,
    updateData,
    reset
  } = useInfiniteScroll(loadLedgerLinesData);

  // Filter ledger lines to show only unreconciled if toggle is active
  const filteredLedgerLines = useMemo(() => {
    return showUnreconciledOnly
      ? ledgerLines.filter(line => line.cleared !== true)
      : ledgerLines;
  }, [ledgerLines, showUnreconciledOnly]);

  // #1933: the just-saved line renders first until the user acts (next add,
  // filter toggle, reload) or it leaves the rendered set. Presentation only:
  // filteredLedgerLines keeps its sort order; the line is drawn once, at the top.
  const { pinnedIndex, pinnedIndexes, pin: pinLine, release: releasePin } = usePinnedLine(filteredLedgerLines);

  // Fill the viewport: if the loaded rows do not reach the bottom of the screen,
  // keep loading earlier months so the infinite-scroll observer has room to fire.
  // Mirrors BudgetDetail. Row heights are unchanged, so the trigger point holds.
  useEffect(() => {
    const fillViewport = async () => {
      if (initialLoading || scrollLoading || loadingMore || !hasMore || autoLoadPaused) return;
      const container = tableContainerRef.current;
      if (!container) return;
      const viewportHeight = window.innerHeight;
      const containerRect = container.getBoundingClientRect();
      const contentHeight = container.scrollHeight;
      const availableSpace = viewportHeight - containerRect.top;
      if (contentHeight < availableSpace + 100 && ledgerLines.length > 0) {
        await loadMore();
      }
    };
    fillViewport();
  }, [initialLoading, scrollLoading, loadingMore, hasMore, autoLoadPaused, ledgerLines.length, loadMore]);

  // New row state (UI form shape; translated to v2 on submit)
  const [newRowData, setNewRowData] = useState({
    dateStamp: getLocalDateString(),
    payeeId: '',
    debit: '',
    credit: '',
    budgetId: '0',
    cleared: false,
    memo: '',
  });
  const [newRowPayeeInput, setNewRowPayeeInput] = useState('');
  // #1520: budget display text lifted to parent (payee-parity) so the save
  // handler can clear it; the child Autocomplete cannot reset its own text.
  const [newRowBudgetInput, setNewRowBudgetInput] = useState('');
  const [savingNewRow, setSavingNewRow] = useState(false);
  // Split (#2263): the drawer's parts, the last one's amount derived from the
  // add row's total. Closing discards them.
  const emptySplitPart = () => ({ debit: '', credit: '', budgetId: '0', input: '' });
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitParts, setSplitParts] = useState(() => [emptySplitPart(), emptySplitPart()]);
  const handleSplitToggle = useCallback(() => {
    setSplitOpen((open) => !open);
    setSplitParts([emptySplitPart(), emptySplitPart()]);
  }, []);
  const handleSplitPartChange = useCallback((index, patch) => {
    setSplitParts((parts) => parts.map((part, i) => (i === index ? { ...part, ...patch } : part)));
  }, []);
  const partCentsOf = (part) => {
    const debit = part.debit !== '' ? parseAmountInput(part.debit) : 0;
    const credit = part.credit !== '' ? parseAmountInput(part.credit) : 0;
    return Math.round((credit - debit) * 100);
  };
  const newRowTotalCents = Math.round(
    ((newRowData.credit !== '' ? parseAmountInput(newRowData.credit) : 0)
      - (newRowData.debit !== '' ? parseAmountInput(newRowData.debit) : 0)) * 100
  );
  const typedSplitCents = splitParts.slice(0, -1).map(partCentsOf);
  const lastSplitCents = newRowTotalCents - typedSplitCents.reduce((sum, c) => sum + c, 0);

  // Tooltip states for validation
  const [showEditPayeeTooltip, setShowEditPayeeTooltip] = useState(false);
  const [showNewRowPayeeTooltip, setShowNewRowPayeeTooltip] = useState(false);

  // In-place Entry Type control (#1938): one prompt per payee field, opened
  // only for a payee with no default. Save reads it through entryTypeForSave.
  // #2289: a Credit Card ledger takes no Income line.
  const incomeAllowed = ledger?.type !== 2;
  const newRowPrompt = useEntryTypePrompt({ payees, incomeAllowed });
  const editPrompt = useEntryTypePrompt({ payees, incomeAllowed });
  // #2297: what the server would resolve for a line, as far as the page can
  // tell (explicit type, else the payee default, else Spending); a card
  // ledger refuses Income here, before any request.
  const incomeRefusedHere = useCallback((lineType, payee, paycheck = false) => !incomeAllowed
    && (paycheck || (lineType ?? payee?.defaultEntryType ?? DEFAULT_ENTRY_TYPE) === 'income'), [incomeAllowed]);
  const {
    onPayeePicked: editPromptPick, onPayeeText: editPromptText, reset: editPromptReset,
    seed: editPromptSeed, markCreated: editPromptMarkCreated,
  } = editPrompt;
  const {
    onPayeePicked: newRowPromptPick, onPayeeText: newRowPromptText, reset: newRowPromptReset,
  } = newRowPrompt;
  // The chips the mobile Sheet and card controls anchor to (#1938).
  const [sheetChipAnchor, setSheetChipAnchor] = useState(null);
  const [cardChipAnchor, setCardChipAnchor] = useState(null);

  // Write a payee's default Entry Type once (#1938) and keep every local copy
  // in step, so the next entry on that payee does not prompt again. A new
  // payee set up as a source of income (#2288) gets the mark instead; the
  // API sets its Income default in the same write.
  const setPayeeDefault = useCallback(async (payee, defaultEntryType, incomeProvider = false) => {
    const response = await api.put(
      activeGroupId,
      `/payees/${encodeURIComponent(payee.payeeId)}`,
      incomeProvider ? { incomeProvider: true } : { defaultEntryType }
    );
    const updated = {
      ...payee,
      ...(response?.payee || {}),
      defaultEntryType: incomeProvider ? 'income' : defaultEntryType,
      ...(incomeProvider ? { incomeProvider: true } : {}),
    };
    setPayees(prev => prev.map(p => (p.payeeId === updated.payeeId ? { ...p, ...updated } : p)));
    await payeeCacheService.invalidatePayeeCache('default_changed');
    return updated;
  }, [activeGroupId]);

  const loadLedgerData = useCallback(async () => {
    try {
      setInitialLoading(true);
      setError(null);
      // A reload settles the pinned line into date order (#1933).
      releasePin();

      if (!activeGroupId) return;

      const [ledgerResponse, balanceResponse] = await Promise.all([
        api.get(activeGroupId, `/ledgers/${encodeURIComponent(id)}`),
        api.get(activeGroupId, `/ledgers/${encodeURIComponent(id)}/balance`),
      ]);

      setLedger(ledgerResponse.ledger);
      setBalance(balanceResponse);

      // Cursor pagination loads the newest page first (replaces the old
      // /lines/recent-month probe + month window).
      await loadInitialData();

    } catch (err) {
      console.error('Failed to load ledger data:', err);
      setError('Failed to load ledger data. Please try again.');
    } finally {
      setInitialLoading(false);
    }
  }, [id, loadInitialData, releasePin]);

  const handleUnarchive = useCallback(async () => {
    if (!ledger) return;
    try {
      setUnarchiving(true);
      setError(null);
      const response = await api.put(activeGroupId, `/ledgers/${encodeURIComponent(ledger.ledgerId)}`, {
        archived: false,
        version: ledger.version
      });
      setLedger(response.ledger);
    } catch (err) {
      console.error('Failed to unarchive ledger:', err);
      setError(err.message || 'Failed to unarchive ledger.');
    } finally {
      setUnarchiving(false);
    }
  }, [ledger, activeGroupId]);

  useEffect(() => {
    // Only load data after authentication is confirmed and GroupContext has
    // resolved a groupId (#1342: on direct-load/refresh the effect fired
    // before the groupId existed, so the API calls threw).
    if (isAuthReady && activeGroupId) {
      loadLedgerData();
      loadBudgetsAndPayees();
    }
  }, [id, loadLedgerData, isAuthReady, activeGroupId]);

  // Focus management - set initial focus to date field when page loads
  useEffect(() => {
    if (isAuthReady && !initialLoading && dateFieldRef.current) {
      // Small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        dateFieldRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthReady, initialLoading]);

  // Global keyboard handler for spacebar to focus date field
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      // Only handle spacebar if no input is focused and not in editing mode
      if (event.key === ' ' && 
          !editingLine && 
          document.activeElement?.tagName !== 'INPUT' && 
          document.activeElement?.tagName !== 'TEXTAREA' &&
          !document.activeElement?.closest('[role="combobox"]')) {
        event.preventDefault();
        dateFieldRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [editingLine]);

  const loadBudgetsAndPayees = async () => {
    if (!activeGroupId) return;
    try {
      // Load payees first (priority) then budgets
      // Try to get payees from cache first
      let cachedPayees = await payeeCacheService.getAllPayees();
      if (!cachedPayees) {
        const response = await api.get(activeGroupId, '/payees');
        const fetchedPayees = response.payees || [];
        await payeeCacheService.cachePayees(fetchedPayees);
        cachedPayees = { payees: fetchedPayees };
      }
      setPayees(cachedPayees.payees);

      // #2116: the cache holds the whole /budgets body, categories included,
      // so a cache hit can still label options with their category acronym.
      let budgetsBody = getCachedList(BUDGETS_LIST_CACHE_KEY);
      if (!budgetsBody?.budgets) {
        const budgetsResponse = await api.get(activeGroupId, '/budgets');
        budgetsBody = {
          budgets: budgetsResponse.budgets || [],
          budgetCategories: budgetsResponse.budgetCategories || [],
        };
        setCachedList(BUDGETS_LIST_CACHE_KEY, budgetsBody);
      }
      const budgetAcronymOf = makeBudgetAcronymOf(budgetsBody.budgetCategories);
      const availableBudgets = budgetsBody.budgets
        .filter(budget => budget.budgetId !== '0' && budget.archived !== true)
        .map(budget => ({ ...budget, categoryAcronym: budgetAcronymOf(budget) }));
      setBudgets(availableBudgets);

      // Other ledgers a line can be moved to (#760): exclude this ledger + archived.
      const ledgersResponse = await api.get(activeGroupId, '/ledgers');
      setAllLedgers(ledgersResponse.ledgers || []);
      const otherLedgers = (ledgersResponse.ledgers || []).filter(
        l => l.ledgerId !== id && l.archived !== true
      );
      setLedgers(otherLedgers);
    } catch (err) {
      console.error('Failed to load budgets and payees:', err);
    }
  };

  // Memoize payee lookup map for fast access (keyed by payeeId string).
  const payeeMap = useMemo(() => {
    const map = new Map();
    payees.forEach(payee => {
      map.set(payee.payeeId, payee);
    });
    return map;
  }, [payees]);

  // Picker options (#1944): the Ledgers group, then up to 15 payees, both
  // startsWith-matched on the field's text.
  const archivedLedgerIds = useMemo(
    () => new Set(allLedgers.filter(l => l.archived).map(l => l.ledgerId)),
    [allLedgers]
  );
  const filteredPayees = useMemo(
    () => payeeOptions(payees, payeeInputValue, { ledgerId: id, archivedLedgerIds, incomeAllowed }),
    [payees, payeeInputValue, id, archivedLedgerIds, incomeAllowed]
  );
  const filteredNewRowPayees = useMemo(
    () => payeeOptions(payees, newRowPayeeInput, { ledgerId: id, archivedLedgerIds, incomeAllowed }),
    [payees, newRowPayeeInput, id, archivedLedgerIds, incomeAllowed]
  );

  // Compute running balances from current balance and visible lines
  const runningBalances = useMemo(() => {
    return computeRunningBalances((balance?.balanceCents ?? 0) / 100, filteredLedgerLines);
  }, [balance?.balanceCents, filteredLedgerLines]);

  // Render order (#1933): the pinned line first, then the rest in sort order.
  // Entries are indexes into filteredLedgerLines so running balances line up.
  // The scroll sentinel goes on the last entry, so a pinned line that happens
  // to be the oldest loaded row never carries it to the top of the page.
  const renderOrder = useMemo(() => {
    const pinnedSet = new Set(pinnedIndexes);
    const rest = filteredLedgerLines.map((_, idx) => idx).filter((idx) => !pinnedSet.has(idx));
    return [...pinnedIndexes, ...rest];
  }, [filteredLedgerLines, pinnedIndexes]);
  const sentinelIndex = renderOrder.length > 0 ? renderOrder[renderOrder.length - 1] : -1;

  // v4 mobile (#1377 board): date-grouped rows. Lines arrive sorted desc, so
  // each group's first row carries that date's end-of-day running balance.
  // The pinned line (#1933) is its own group at the top; it stays in its
  // date's header balance (the end-of-day figure does not change) and only
  // its card is left out of that group.
  const mobileGroups = useMemo(() => {
    if (!useCardView) return [];
    const groups = [];
    let current = null;
    filteredLedgerLines.forEach((line, idx) => {
      if (!current || current.date !== line.dateStamp) {
        current = { key: line.dateStamp, date: line.dateStamp, balance: runningBalances[idx], rows: [] };
        groups.push(current);
      }
      if (!pinnedIndexes.includes(idx)) {
        current.rows.push({ line, runningBalance: runningBalances[idx], index: idx });
      }
    });
    const dated = groups.filter((g) => g.rows.length > 0);
    if (pinnedIndex === -1) return dated;
    const pinned = filteredLedgerLines[pinnedIndex];
    return [
      {
        key: 'pinned',
        pinned: true,
        date: pinned.dateStamp,
        balance: runningBalances[pinnedIndex],
        rows: pinnedIndexes.map((idx) => ({ line: filteredLedgerLines[idx], runningBalance: runningBalances[idx], index: idx })),
      },
      ...dated,
    ];
  }, [useCardView, filteredLedgerLines, runningBalances, pinnedIndex, pinnedIndexes]);

  // Last bill paid (non-checking only): the shared helper (#1944) prefers a
  // payment from a Checking ledger's payee.
  const lastBillPaid = useMemo(() => {
    if (ledger?.type === 0) return null;
    return calculateLastBillPaid(ledgerLines, checkingLedgerNames(allLedgers));
  }, [ledgerLines, ledger?.type, allLedgers]);

  const handleUnreconciledToggle = () => {
    setShowUnreconciledOnly(!showUnreconciledOnly);
    // A filter change settles the pinned line into date order (#1933).
    releasePin();
  };

  const handleEditClick = useCallback((line) => {
    const amt = typeof line.amountCents === 'number' ? line.amountCents : 0;
    const currentPayee = line.payeeId ? payeeMap.get(line.payeeId) : null;
    setEditingLine(line.lineId);
    setEditData({
      dateStamp: line.dateStamp,
      payeeId: line.payeeId || '',
      debit: amt < 0 ? (-amt / 100).toFixed(2) : '',
      credit: amt > 0 ? (amt / 100).toFixed(2) : '',
      budgetId: line.budgetId || '0',
      memo: line.memo || '',
    });
    setPayeeInputValue(currentPayee ? currentPayee.name : (line.payeeName || ''));
    // Entering edit mode opens nothing (#1938); the chip shows the line's own
    // type. A PAYCHECK line (#2288) preselects the payee's Paycheck option.
    editPromptSeed({
      payee: currentPayee, name: line.payeeName, lineType: line.entryType,
      paycheck: line.lineSubtype === PAYCHECK_SUBTYPE,
    });
  }, [payeeMap, editPromptSeed]);

  const handleEditCancel = useCallback(() => {
    setEditingLine(null);
    setEditData({});
    setPayeeInputValue('');
    editPromptReset();
  }, [editPromptReset]);

  const handlePayeeChange = useCallback((event, newValue) => {
    // freeSolo can deliver a raw string (Enter with no highlighted option);
    // leave payeeId alone then - save resolves from the input text (#1621).
    if (newValue && typeof newValue === 'object') {
      setEditData(prev => ({ ...prev, payeeId: newValue.payeeId }));
      editPromptPick(newValue);
    } else if (!newValue) {
      setEditData(prev => ({ ...prev, payeeId: '' }));
      editPromptReset();
    }
    // Hide tooltip when user selects a payee
    setShowEditPayeeTooltip(false);
  }, [editPromptPick, editPromptReset]);

  const handlePayeeInputChange = useCallback((event, newInputValue, reason) => {
    setPayeeInputValue(newInputValue);
    // Typing closes and re-arms the control; MUI's own 'reset' after a pick
    // or a value change is not the user typing (#1938).
    if (reason !== 'reset') editPromptText(newInputValue);
  }, [editPromptText]);

  // New row handlers
  const handleNewRowPayeeChange = useCallback((event, newValue) => {
    // Same freeSolo raw-string guard as handlePayeeChange (#1621).
    if (newValue && typeof newValue === 'object') {
      setNewRowData(prev => ({ ...prev, payeeId: newValue.payeeId }));
      newRowPromptPick(newValue);
    } else if (!newValue) {
      setNewRowData(prev => ({ ...prev, payeeId: '' }));
      newRowPromptReset();
    }
    setShowNewRowPayeeTooltip(false);
  }, [newRowPromptPick, newRowPromptReset]);

  const handleNewRowPayeeInputChange = useCallback((event, newInputValue, reason) => {
    setNewRowPayeeInput(newInputValue);
    if (reason !== 'reset') newRowPromptText(newInputValue);
  }, [newRowPromptText]);

  const handleNewRowBudgetInputChange = useCallback((event, newInputValue) => {
    setNewRowBudgetInput(newInputValue);
  }, []);

  const handleCreatePayeeFromNewRow = async (payeeName) => {
    try {
      setSavingNewRow(true);
      const response = await api.post(activeGroupId, '/payees', { name: payeeName });
      const newPayee = response.payee;

      payeeCacheService.recordPayeeUsage(newPayee.name);
      await payeeCacheService.invalidatePayeeCache('new_payee_added');
      setPayees(prev => {
        const updated = [...prev, newPayee];
        return updated.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      });

      setNewRowData(prev => ({ ...prev, payeeId: newPayee.payeeId }));
      setNewRowPayeeInput(newPayee.name);
      // Created without a default: the control opens as "New payee" (#1938).
      newRowPrompt.markCreated(newPayee);
      newRowPromptPick(newPayee);
    } catch (err) {
      console.error('Failed to create payee:', err);
      setError('Failed to create new payee. Please try again.');
    } finally {
      setSavingNewRow(false);
    }
  };

  // #1577: the desktop payee tooltip (NewLedgerRow) and the full-page
  // setError Alert are invisible or destructive under the mobile Sheet, so
  // the Sheet save pre-validates and fires bottom-anchored toasts instead.
  // Conditions mirror handleSaveNewRow exactly; typed-but-unselected payee
  // text passes (handleSaveNewRow resolves or creates that payee). Messages
  // match BudgetDetail's edit-save toasts.
  const collectQuickAddErrors = () => {
    const errors = [];
    if ((!newRowData.payeeId || newRowData.payeeId === '') && !newRowPayeeInput.trim()) {
      errors.push('Payee is required');
    }
    if ((!newRowData.debit || newRowData.debit === '') && (!newRowData.credit || newRowData.credit === '')) {
      errors.push('Please enter either a debit or credit amount');
    }
    if (!isValidDateStamp(newRowData.dateStamp)) {
      errors.push(INVALID_DATE_MESSAGE);
    }
    // #2288: a paycheck is a deposit.
    if (newRowPrompt.paycheck && newRowData.debit && newRowData.debit !== '') {
      errors.push(engineStrings['entryType.paycheckDebit']);
    }
    // #2297: a card ledger takes no Income line; say so before any request.
    const sheetPayee = payees.find(p => p.payeeId === newRowData.payeeId)
      || payees.find(p => p.name.toLowerCase() === newRowPayeeInput.trim().toLowerCase())
      || null;
    const sheetPlan = entryTypeForSave({ entry: newRowPrompt.entry, payee: sheetPayee, payeeName: newRowPayeeInput.trim(), incomeAllowed });
    if (incomeRefusedHere(sheetPlan.lineType, sheetPayee, newRowPrompt.paycheck)) {
      errors.push(engineStrings['entryType.noIncomeOnCard']);
    }
    return errors;
  };

  const handleSaveNewRow = async () => {
    try {
      setSavingNewRow(true);

      // Resolve payee: existing, newly-typed, or find-by-name.
      let payeeId = newRowData.payeeId;
      let selectedPayee = payeeId ? payees.find(p => p.payeeId === payeeId) || null : null;
      const typed = newRowPayeeInput.trim();

      // Entry Type (#1938): read the control once; a payee with no default
      // gets one at this save and the line is stamped with the same value.
      const promptEntry = newRowPrompt.entry;
      // #2288: the Paycheck option, or a new payee set up as a source of
      // income: the line is stamped PAYCHECK and must be a deposit.
      const paycheck = newRowPrompt.paycheck;
      let entryType = null;
      let createdNow = false;

      // Typed text is authoritative (#1621): non-empty input that does not
      // match the selected payee's name resolves by text (find or create),
      // so select-then-append keeps the appended text.
      if (typed && (!payeeId || typed.toLowerCase() !== (selectedPayee?.name || '').toLowerCase())) {
        const existingPayee = payees.find(p => p.name.toLowerCase() === typed.toLowerCase());
        if (existingPayee) {
          payeeId = existingPayee.payeeId;
          selectedPayee = existingPayee;
          setNewRowData(prev => ({ ...prev, payeeId: existingPayee.payeeId }));
        } else {
          try {
            const plan = entryTypeForSave({ entry: promptEntry, payee: null, payeeName: typed, incomeAllowed });
            entryType = plan.lineType;
            if (incomeRefusedHere(entryType, null, paycheck)) {
              setError(engineStrings['entryType.noIncomeOnCard']);
              setSavingNewRow(false);
              setTimeout(() => setError(null), 3000);
              return false;
            }
            createdNow = true;
            const response = await api.post(activeGroupId, '/payees', {
              name: typed,
              defaultEntryType: plan.payeeDefault,
              ...(plan.incomeProvider === true ? { incomeProvider: true } : {}),
            });
            const newPayee = response.payee;
            payeeCacheService.recordPayeeUsage(newPayee.name);
            await payeeCacheService.invalidatePayeeCache('new_payee_added');
            setPayees(prev => {
              const updated = [...prev, newPayee];
              return updated.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            });
            payeeId = newPayee.payeeId;
            selectedPayee = newPayee;
            setNewRowData(prev => ({ ...prev, payeeId: newPayee.payeeId }));
          } catch (err) {
            console.error('Failed to create payee:', err);
            setError('Failed to create new payee. Please try again.');
            setSavingNewRow(false);
            return false;
          }
        }
      }

      if (!payeeId || payeeId === '') {
        setShowNewRowPayeeTooltip(true);
        setSavingNewRow(false);
        setTimeout(() => setShowNewRowPayeeTooltip(false), 2000);
        return false;
      }

      if ((!newRowData.debit || newRowData.debit === '') && (!newRowData.credit || newRowData.credit === '')) {
        setError('Please enter either a debit or credit amount.');
        setSavingNewRow(false);
        setTimeout(() => setError(null), 3000);
        return false;
      }

      // This path had no date check before #1842, so a mistyped year reached
      // the backend and landed the line in its own yearMonth partition.
      if (!isValidDateStamp(newRowData.dateStamp)) {
        setError(INVALID_DATE_MESSAGE);
        setSavingNewRow(false);
        setTimeout(() => setError(null), 3000);
        return false;
      }

      if (!selectedPayee) {
        setError('Unable to find payee information. Please try again.');
        setSavingNewRow(false);
        return false;
      }

      if (!createdNow) {
        const plan = entryTypeForSave({ entry: promptEntry, payee: selectedPayee, incomeAllowed });
        entryType = plan.lineType;
        // #2297: refused here on a card ledger, before the payee default write.
        if (incomeRefusedHere(entryType, selectedPayee, paycheck)) {
          setError(engineStrings['entryType.noIncomeOnCard']);
          setSavingNewRow(false);
          setTimeout(() => setError(null), 3000);
          return false;
        }
        if (plan.payeeDefault !== null) {
          selectedPayee = await setPayeeDefault(selectedPayee, plan.payeeDefault, plan.incomeProvider === true);
        }
      }

      const debit = newRowData.debit !== '' ? parseAmountInput(newRowData.debit) : 0;
      const credit = newRowData.credit !== '' ? parseAmountInput(newRowData.credit) : 0;
      const amountCents = Math.round((credit - debit) * 100);

      if (paycheck && amountCents <= 0) {
        setError(engineStrings['entryType.paycheckDebit']);
        setSavingNewRow(false);
        setTimeout(() => setError(null), 3000);
        return false;
      }

      const body = {
        sourceType: 'ledger_line',
        ledgerId: String(id),
        dateStamp: newRowData.dateStamp,
        amountCents,
        memo: sanitizeMemo(newRowData.memo || ''),
        payeeId: String(payeeId),
      };
      if (!splitOpen && newRowData.budgetId && newRowData.budgetId !== '0') {
        body.budgetId = String(newRowData.budgetId);
      }
      if (newRowData.cleared === true) {
        body.cleared = true;
      }
      if (entryType !== null) {
        body.entryType = entryType;
      }
      if (paycheck) {
        body.lineSubtype = PAYCHECK_SUBTYPE;
      }
      // Split (#2263): every typed part nonzero, in the total's direction and
      // smaller than it, so the derived last part is nonzero too.
      if (splitOpen) {
        const sign = Math.sign(amountCents);
        const cents = [...typedSplitCents, lastSplitCents];
        if (cents.some((c) => c === 0 || Math.sign(c) !== sign)) {
          setError('Each split part needs an amount smaller than the total, in the same direction.');
          setSavingNewRow(false);
          setTimeout(() => setError(null), 3000);
          return false;
        }
        body.splits = cents.map((c, i) => {
          const part = { amountCents: c };
          const budgetId = splitParts[i].budgetId;
          if (budgetId && budgetId !== '0') part.budgetId = String(budgetId);
          return part;
        });
      }

      const response = await api.post(activeGroupId, '/lines', body);
      const newLine = response.line;
      // A split saved every part (#2263); pin them all for the spot check.
      const newLines = response.lines || (newLine ? [newLine] : []);
      notifyTangibleRefresh();
      // #1990: a transfer to another ledger also wrote that ledger's side.
      // The one visible success toast (DESIGN_SYSTEM.md 8.10).
      if (response.mirror) {
        showSuccess(interpolate(engineStrings['ledgerPayees.mirrorAdded'], {
          amount: formatCurrency(response.mirror.amountCents / 100),
          ledger: response.mirror.ledgerName,
        }), { visible: true });
      }

      setNewRowData({
        dateStamp: getLocalDateString(),
        payeeId: '',
        debit: '',
        credit: '',
        budgetId: '0',
        cleared: false,
        memo: '',
      });
      setNewRowPayeeInput('');
      setNewRowBudgetInput('');
      newRowPromptReset();
      setSplitOpen(false);
      setSplitParts([emptySplitPart(), emptySplitPart()]);

      // Focus back to date field for next entry
      setTimeout(() => {
        dateFieldRef.current?.focus();
      }, 100);

      // Optimistically update the UI - enhance the line with display-friendly data
      if (newLines.length > 0) {
        // updateData re-sorts, so a past-dated line lands in date order (#1845).
        updateData(prevLines => [...newLines, ...prevLines]);
        // Keep them in view for the spot check until the next entry (#1933).
        pinLine(newLines.map((l) => l.lineId));
      } else {
        reset();
      }

      const preWriteCents = balanceRef.current?.balanceCents ?? 0;
      bumpBalance({ balanceDelta: amountCents, clearedDelta: body.cleared === true ? amountCents : 0 });
      reconcileBalance(preWriteCents, amountCents);
      // Success signal so the mobile quick-add Sheet can close (#1377).
      return true;

    } catch (err) {
      console.error('Failed to save new ledger line:', err);
      // Revert optimistic updates on error by reloading data. The reload
      // clears the page error, so the message goes up after it (#2297), the
      // API's own refusal text when it sent one.
      await loadLedgerData();
      setError(err?.body?.error || 'Failed to save new transaction. Please try again.');
      return false;
    } finally {
      setSavingNewRow(false);
    }
  };

  const handleEditSave = useCallback(async (line) => {
    try {
      setSaving(true);

      // Resolve payee (select existing, look up by name, or create).
      let payeeId = editData.payeeId;
      let resolvedPayee = payeeId ? payees.find(p => p.payeeId === payeeId) || null : null;
      const typed = payeeInputValue.trim();

      // Entry Type (#1938): the payee default is settled only when the payee
      // field was committed or changed; a memo or amount edit leaves it alone.
      const promptEntry = editPrompt.entry;
      // #2288: on the Paycheck option now, and was the line a paycheck before.
      const paycheckNow = editPrompt.paycheck;
      const paycheckWas = line.lineSubtype === PAYCHECK_SUBTYPE;
      let entryType = null;
      let createdNow = false;

      // Typed text is authoritative (#1621): non-empty input that does not
      // match the selected payee's name resolves by text (find or create),
      // so select-then-append keeps the appended text.
      if (typed && (!payeeId || typed.toLowerCase() !== (resolvedPayee?.name || '').toLowerCase())) {
        const existingPayee = payees.find(p => p.name.toLowerCase() === typed.toLowerCase());
        if (existingPayee) {
          payeeId = existingPayee.payeeId;
          resolvedPayee = existingPayee;
          setEditData(prev => ({ ...prev, payeeId: existingPayee.payeeId }));
        } else {
          try {
            const plan = entryTypeForSave({ entry: promptEntry, payee: null, payeeName: typed, incomeAllowed });
            entryType = plan.lineType;
            if (incomeRefusedHere(entryType, null, paycheckNow)) {
              setError(engineStrings['entryType.noIncomeOnCard']);
              setSaving(false);
              setTimeout(() => setError(null), 3000);
              return;
            }
            createdNow = true;
            const response = await api.post(activeGroupId, '/payees', {
              name: typed,
              defaultEntryType: plan.payeeDefault,
              ...(plan.incomeProvider === true ? { incomeProvider: true } : {}),
            });
            const newPayee = response.payee;
            payeeCacheService.recordPayeeUsage(newPayee.name);
            await payeeCacheService.invalidatePayeeCache('new_payee_added');
            setPayees(prev => {
              const updated = [...prev, newPayee];
              return updated.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            });
            payeeId = newPayee.payeeId;
            resolvedPayee = newPayee;
            setEditData(prev => ({ ...prev, payeeId: newPayee.payeeId }));
          } catch (err) {
            console.error('Failed to create payee:', err);
            setError('Failed to create new payee. Please try again.');
            setSaving(false);
            return;
          }
        }
      }

      if (!payeeId || payeeId === '') {
        setShowEditPayeeTooltip(true);
        setSaving(false);
        setTimeout(() => setShowEditPayeeTooltip(false), 2000);
        return;
      }

      if ((!editData.debit || editData.debit === '') && (!editData.credit || editData.credit === '')) {
        setError('Please enter either a debit or credit amount.');
        setSaving(false);
        setTimeout(() => setError(null), 3000);
        return;
      }

      if (!isValidDateStamp(editData.dateStamp)) {
        setError(INVALID_DATE_MESSAGE);
        setSaving(false);
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Settle only when the user touched the control or changed the payee; a
      // memo or amount edit leaves the line's type and the payee alone.
      const settle = Boolean(promptEntry?.touched) || String(payeeId) !== String(line.payeeId || '');
      if (!createdNow && resolvedPayee) {
        const plan = entryTypeForSave({ entry: promptEntry, payee: resolvedPayee, settle, incomeAllowed });
        entryType = plan.lineType;
        // #2297: an edit that settles the type on a card ledger is refused
        // here; an untouched line keeps what it has and sends no type.
        if (settle && incomeRefusedHere(entryType, resolvedPayee, paycheckNow)) {
          setError(engineStrings['entryType.noIncomeOnCard']);
          setSaving(false);
          setTimeout(() => setError(null), 3000);
          return;
        }
        if (plan.payeeDefault !== null) {
          resolvedPayee = await setPayeeDefault(resolvedPayee, plan.payeeDefault, plan.incomeProvider === true);
        }
      }

      const debit = editData.debit !== '' ? parseAmountInput(editData.debit) : 0;
      const credit = editData.credit !== '' ? parseAmountInput(editData.credit) : 0;
      const amountCents = Math.round((credit - debit) * 100);
      if (paycheckNow && amountCents <= 0) {
        setError(engineStrings['entryType.paycheckDebit']);
        setSaving(false);
        setTimeout(() => setError(null), 3000);
        return;
      }
      // #2288: sent only when the stamp moves, or a paycheck changes payee
      // (the API checks the new payee's mark); an untouched line keeps it.
      const payeeChanged = String(payeeId) !== String(line.payeeId || '');
      const lineSubtype = paycheckNow !== paycheckWas || (paycheckNow && payeeChanged)
        ? (paycheckNow ? PAYCHECK_SUBTYPE : null)
        : undefined;
      const budgetIdStr = editData.budgetId && editData.budgetId !== '0'
        ? String(editData.budgetId)
        : null;

      const oldAmt = typeof line.amountCents === 'number' ? line.amountCents : 0;
      const balanceDeltaCents = amountCents - oldAmt;

      // resolvedPayee, not a payees.find(): a just-created payee is not in
      // this render's payees closure yet (#1621 optimistic-display fix).
      const payeeForDisplay = resolvedPayee;

      updateData(prevLines =>
        prevLines.map(l =>
          l.lineId === line.lineId
            ? {
                ...l,
                dateStamp: editData.dateStamp,
                yearMonth: editData.dateStamp.slice(0, 7),
                amountCents,
                payeeId: String(payeeId),
                payeeName: payeeForDisplay ? payeeForDisplay.name : (l.payeeName || ''),
                memo: sanitizeMemo(editData.memo || ''),
                // A retyped entry shows its new chip at once (#1943); the
                // response merge below confirms it.
                ...(entryType !== null ? { entryType } : {}),
                ...(lineSubtype === PAYCHECK_SUBTYPE ? { lineSubtype: PAYCHECK_SUBTYPE, entryType: 'income' } : {}),
                ...(lineSubtype === null ? { lineSubtype: undefined } : {}),
                ...(budgetIdStr
                  ? { budgetId: budgetIdStr, budgetName: (budgets.find(b => b.budgetId === budgetIdStr) || {}).name || l.budgetName || '' }
                  : { budgetId: undefined, budgetName: '' }),
              }
            : l
        )
      );

      const preWriteCents = balanceRef.current?.balanceCents ?? 0;
      bumpBalance({ balanceDelta: balanceDeltaCents, clearedDelta: line.cleared === true ? balanceDeltaCents : 0 });
      reconcileBalance(preWriteCents, balanceDeltaCents);

      setEditingLine(null);
      setEditData({});
      setPayeeInputValue('');
      editPromptReset();

      // Brief flash on the saved row (parity with BudgetDetail).
      setHighlightedLineId(line.lineId);
      setTimeout(() => setHighlightedLineId(null), 800);

      const yearMonth = line.yearMonth || line.dateStamp.slice(0, 7);
      const body = {
        version: line.version,
        dateStamp: editData.dateStamp,
        amountCents,
        payeeId: String(payeeId),
        budgetId: budgetIdStr, // null clears, id sets
        memo: sanitizeMemo(editData.memo || ''), // always sent; '' clears (#1549)
      };
      if (entryType !== null) body.entryType = entryType;
      if (lineSubtype !== undefined) body.lineSubtype = lineSubtype;

      const response = await api.put(
        activeGroupId,
        `/lines/${encodeURIComponent(yearMonth)}/${encodeURIComponent(line.lineId)}`,
        body
      );

      // Merge bumped version (and any other server-side changes) back into
      // local state so a rapid follow-up edit reads fresh version (#639).
      const updated = response?.line;
      if (updated) {
        updateData(prevLines =>
          prevLines.map(l => l.lineId === line.lineId ? { ...l, ...updated } : l)
        );
      }
      notifyTangibleRefresh();

    } catch (err) {
      console.error('Failed to save ledger line:', err);
      // Revert optimistic update on error; the reload clears the page error,
      // so the message goes up after it (#2297).
      await loadLedgerData();
      setError(err?.body?.error || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editData, loadLedgerData, payeeInputValue, payees, budgets, activeGroupId, id, updateData, reconcileBalance, bumpBalance, editPrompt.entry, editPrompt.paycheck, engineStrings, setPayeeDefault, editPromptReset, incomeAllowed, incomeRefusedHere]);

  const handleDeleteClick = useCallback((line) => {
    setDeleteDialog({ open: true, line });
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialog({ open: false, line: null });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const line = deleteDialog.line;
    setDeleteDialog({ open: false, line: null });
    if (!line) return;

    try {
      const amtCents = typeof line.amountCents === 'number' ? line.amountCents : 0;

      updateData(prevLines =>
        prevLines.filter(l => l.lineId !== line.lineId)
      );

      const preWriteCents = balanceRef.current?.balanceCents ?? 0;
      bumpBalance({ balanceDelta: -amtCents, clearedDelta: line.cleared === true ? -amtCents : 0 });

      const yearMonth = line.yearMonth || line.dateStamp.slice(0, 7);
      await api.delete(
        activeGroupId,
        `/lines/${encodeURIComponent(yearMonth)}/${encodeURIComponent(line.lineId)}?version=${line.version}`
      );
      notifyTangibleRefresh();
      reconcileBalance(preWriteCents, -amtCents);

    } catch (error) {
      console.error('Failed to delete ledger line:', error);
      setError('Failed to delete ledger line. Please try again.');

      // Revert optimistic updates on error by reloading data
      await loadLedgerData();
    }
  }, [activeGroupId, reconcileBalance, bumpBalance, deleteDialog]);

  // "Move to ledger" (#760): open the picker dialog for a line.
  const handleMoveClick = useCallback((line) => {
    setMoveLine(line);
    setMoveTargetLedger(null);
  }, []);

  const handleMoveCancel = useCallback(() => {
    if (moving) return;
    setMoveLine(null);
    setMoveTargetLedger(null);
  }, [moving]);

  // Relocate the line to the chosen ledger via the existing line-update endpoint
  // (only version + ledgerId are sent; the backend rekeys the ledger GSI and
  // invalidates both ledgers' balance cache). On success the line leaves this
  // ledger's view; on failure we reload to drop any optimistic state.
  const handleMoveConfirm = useCallback(async () => {
    if (!moveLine || !moveTargetLedger) return;
    const line = moveLine;
    const target = moveTargetLedger;
    setMoving(true);
    try {
      const yearMonth = line.yearMonth || line.dateStamp.slice(0, 7);
      await api.put(
        activeGroupId,
        `/lines/${encodeURIComponent(yearMonth)}/${encodeURIComponent(line.lineId)}`,
        { version: line.version, ledgerId: target.ledgerId }
      );
      notifyTangibleRefresh();

      const amtCents = typeof line.amountCents === 'number' ? line.amountCents : 0;
      const preWriteCents = balanceRef.current?.balanceCents ?? 0;
      updateData(prevLines => prevLines.filter(l => l.lineId !== line.lineId));
      bumpBalance({ balanceDelta: -amtCents, clearedDelta: line.cleared === true ? -amtCents : 0 });

      setMoveLine(null);
      setMoveTargetLedger(null);
      reconcileBalance(preWriteCents, -amtCents);
    } catch (error) {
      console.error('Failed to move ledger line:', error);
      setError(error?.status === 409
        ? 'This line changed since you opened it. Reloading - open the menu and try the move again.'
        : 'Failed to move the line to the selected ledger. Please try again.');
      setMoveLine(null);
      setMoveTargetLedger(null);
      await loadLedgerData();
    } finally {
      setMoving(false);
    }
  }, [moveLine, moveTargetLedger, activeGroupId, id, updateData, loadLedgerData, reconcileBalance, bumpBalance]);

  const handleCreatePayeeFromInput = useCallback(async (payeeName) => {
    try {
      setSaving(true);
      const response = await api.post(activeGroupId, '/payees', { name: payeeName });
      const newPayee = response.payee;

      payeeCacheService.recordPayeeUsage(newPayee.name);
      await payeeCacheService.invalidatePayeeCache('new_payee_added');
      setPayees(prev => {
        const updated = [...prev, newPayee];
        return updated.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      });

      setEditData(prev => ({ ...prev, payeeId: newPayee.payeeId }));
      setPayeeInputValue(newPayee.name);
      editPromptMarkCreated(newPayee);
      editPromptPick(newPayee);
    } catch (err) {
      console.error('Failed to create payee:', err);
      setError('Failed to create new payee. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [activeGroupId, editPromptMarkCreated, editPromptPick]);

  const handleClearToggle = useCallback(async (line) => {
    const lineId = line.lineId;
    const prevCleared = line.cleared === true;
    const nextCleared = !prevCleared;
    // #2070: the Reconciled total moves by the line amount at once; after the
    // PUT a poll watches clearedCents, skipped when the page holds no number
    // (an older backend).
    const amt = typeof line.amountCents === 'number' ? line.amountCents : 0;
    const clearedDelta = nextCleared ? amt : -amt;
    const preCleared = balanceRef.current?.clearedCents;

    try {
      setUpdatingClear(prev => new Set([...prev, lineId]));

      updateData(prevLines =>
        prevLines.map(l => l.lineId === lineId ? { ...l, cleared: nextCleared } : l)
      );
      bumpBalance({ clearedDelta });

      const yearMonth = line.yearMonth || line.dateStamp.slice(0, 7);
      const response = await api.put(
        activeGroupId,
        `/lines/${encodeURIComponent(yearMonth)}/${encodeURIComponent(lineId)}`,
        { version: line.version, cleared: nextCleared }
      );

      // Merge bumped version (and any other server-side changes) back into
      // local state so a rapid re-toggle reads fresh version (#639).
      const updated = response?.line;
      if (updated) {
        updateData(prevLines =>
          prevLines.map(l => l.lineId === lineId ? { ...l, ...updated } : l)
        );
      }
      if (typeof preCleared === 'number') reconcileBalance(preCleared, clearedDelta, 'clearedCents');

    } catch (err) {
      console.error('Failed to update clear status:', err);
      setError('Failed to update reconciliation status. Please try again.');

      updateData(prevLines =>
        prevLines.map(l => l.lineId === lineId ? { ...l, cleared: prevCleared } : l)
      );
      bumpBalance({ clearedDelta: -clearedDelta });
    } finally {
      setUpdatingClear(prev => {
        const newSet = new Set(prev);
        newSet.delete(lineId);
        return newSet;
      });
    }
  }, [activeGroupId, updateData, bumpBalance, reconcileBalance]);

  // Entry Type control views (#1938): what a row or the Sheet needs to render
  // the control and the picker's flyout (#1943); the caption comes from `entry`.
  // #2288: the rows also need the paycheck state and the provider choice;
  // the Sheet and the card editor read the hook directly.
  const newRowPromptView = {
    open: newRowPrompt.open,
    value: newRowPrompt.value,
    setValue: newRowPrompt.setValue,
    onPayeeBlur: newRowPrompt.onPayeeBlur,
    commitText: newRowPrompt.commitText,
    chip: newRowPrompt.chip,
    toggle: newRowPrompt.toggle,
    close: newRowPrompt.close,
    entry: newRowPrompt.entry,
    paycheck: newRowPrompt.paycheck,
    pending: newRowPrompt.pending,
    setProviderChoice: newRowPrompt.setProviderChoice,
    incomeAllowed: newRowPrompt.incomeAllowed,
  };
  const editPromptView = {
    open: editPrompt.open,
    value: editPrompt.value,
    setValue: editPrompt.setValue,
    onPayeeBlur: editPrompt.onPayeeBlur,
    commitText: editPrompt.commitText,
    chip: editPrompt.chip,
    toggle: editPrompt.toggle,
    close: editPrompt.close,
    entry: editPrompt.entry,
    paycheck: editPrompt.paycheck,
    pending: editPrompt.pending,
    setProviderChoice: editPrompt.setProviderChoice,
    incomeAllowed: editPrompt.incomeAllowed,
  };

  // Props only the editing desktop row needs (#1790). Built per render and
  // spread onto that one row; every other row keeps a stable prop set so
  // React.memo holds while the user types.
  const editRowProps = {
    editData,
    setEditData,
    payeeInputValue,
    handlePayeeInputChange,
    handlePayeeChange,
    filteredPayees,
    payees,
    budgets,
    saving,
    handleEditSave,
    handleEditCancel,
    handleCreatePayeeFromInput,
    showEditPayeeTooltip,
    entryPrompt: editPromptView,
  };

  // Full-page centered loader only until the hero has ledger metadata. Once the
  // ledger is in, render the page shell and let the in-table $ spinner cover the
  // line load (so we do not show a big centered spinner for the whole fetch).
  if (!isAuthReady || (initialLoading && !ledger)) {
    return <CenteredLoader message="Loading ledger..." />;
  }

  if (error || scrollError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error || scrollError}
      </Alert>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      {/* #1465 overflow probe: a zero-height, full-width sentinel whose measured
          width drives the table->cards switch. It stays mounted in both layouts,
          so the measurement is never stretched by the wide table itself. */}
      <Box ref={overflowProbeRef} aria-hidden sx={{ height: 0 }} />
      {/* Sticky page hero: ledger name + balance (raw sign); reconciliation
          (reconciled total and unreconciled chip, #2070) + (credit-card-only)
          last bill paid in the right aux slot */}
      {(() => {
        const balanceCents = balance?.balanceCents;
        const hasBalance = balanceCents != null;
        const isCreditCard = ledger?.type === 2;
        const unreconciledCount = ledgerLines.filter((line) => line.cleared !== true).length;

        const unreconciledChip = ledgerLines.length > 0 && unreconciledCount > 0 ? (
          <Chip
            label={<><MaskedNumber value={unreconciledCount} /> Unreconciled</>}
            color="warning"
            variant={showUnreconciledOnly ? 'filled' : 'outlined'}
            size="small"
            clickable
            onClick={handleUnreconciledToggle}
            sx={{
              height: 22,
              '& .MuiChip-label': {
                paddingLeft: '8px',
                paddingRight: '8px',
                fontSize: `${ds.font.meta}px`,
              },
              ...(showUnreconciledOnly && {
                backgroundColor: palette.warning.main,
                color: palette.warning.contrastText,
                '&:hover': { backgroundColor: palette.warning.dark },
              }),
              // Deepened chip: the outlined warning chip is washed on a card, so it
              // gets a filled treatment in both themes (#1119 light amber-800 on faint
              // amber; #1245 dark amber on deep amber-brown). The filled (active) state
              // is unchanged.
              ...(!showUnreconciledOnly && {
                color: palette.warning.deep,
                borderColor: palette.warning.border,
                backgroundColor: palette.warning.chipBg,
              }),
            }}
          />
        ) : null;

        const auxLineSx = {
          fontSize: '12px',
          lineHeight: 1.2,
          color: palette.text.dim,
          fontVariantNumeric: 'tabular-nums',
        };
        const lastBillPaidLine = isCreditCard ? (
          <Typography component="div" sx={auxLineSx}>
            {'Last bill paid '}
            <Box component="span" sx={{ color: palette.text.secondary, fontWeight: ds.font.weightMedium }}>
              {lastBillPaid ? formatDate(lastBillPaid.dateStamp, 'date-only') : '-'}
            </Box>
          </Typography>
        ) : null;

        // #2070: the reconciled total comes from the balance cache (#2069),
        // never from the loaded lines, so unloaded pages and the Unreconciled
        // filter leave it alone. Shown as an absolute value in the Last bill
        // paid grey (owner, 2026-10-19), like the figure on a statement.
        const reconciledCents = balance?.clearedCents;
        const reconciledLine = typeof reconciledCents === 'number' && ledgerLines.length > 0 ? (
          <Typography component="div" data-testid="ledger-reconciled-total" sx={auxLineSx}>
            {'Reconciled '}
            <Box component="span" sx={{ color: palette.text.secondary, fontWeight: ds.font.weightMedium }}>
              <MaskedCurrency value={Math.abs(reconciledCents) / 100} />
            </Box>
          </Typography>
        ) : null;

        // Right-aligned vertical stack: "Last bill paid" (credit cards), the
        // Reconciled total, then the unreconciled chip. No block when none of
        // them renders.
        const aux = (lastBillPaidLine || reconciledLine || unreconciledChip) ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: `${ds.space[1]}px`,
            }}
          >
            {lastBillPaidLine}
            {reconciledLine}
            {unreconciledChip}
          </Box>
        ) : null;

        // v4 detail hero (#1377 board): a single line - back link above, then
        // 19px category glyph + 18px name + 18px sign-colored balance + 12px
        // dim type label. The header ends with the top-bar avatar; no kebab
        // (convention 1).
        const HeroIcon = ledgerTypeIcon(ledger?.type);
        const heroIconColors = iconBoxColors(theme, ledgerTypeCategory(ledger?.type));
        const typeLabel = { 0: 'Checking', 1: 'Savings', 2: 'Credit Card' }[ledger?.type] || '';

        const label = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: `${ds.space[4]}px`, flexWrap: 'wrap', lineHeight: 1.15 }}>
            <Box
              aria-hidden
              sx={{
                color: heroIconColors.accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, '& svg': { width: 19, height: 19 },
              }}
            >
              <HeroIcon />
            </Box>
            <Box component="span" sx={{ fontSize: { xs: '20px', sm: '18px' }, fontWeight: ds.font.weightMedium, color: palette.text.primary }}>
              {ledger?.name}
            </Box>
            <Box
              component="span"
              sx={{
                // Mobile board hero: prominent 26px balance on its own line.
                fontSize: { xs: '26px', sm: '18px' },
                width: { xs: '100%', sm: 'auto' },
                letterSpacing: { xs: ds.font.letterHero, sm: 0 },
                fontWeight: ds.font.weightMedium,
                fontVariantNumeric: 'tabular-nums',
                color: hasBalance
                  ? resolveAmountColor(palette, 'total', balanceCents < 0, balanceCents === 0)
                  : palette.text.dim,
              }}
            >
              {hasBalance ? <MaskedCurrency value={balanceCents / 100} /> : '--'}
            </Box>
            <Box component="span" sx={{ fontSize: '12px', color: palette.text.dim }}>
              {typeLabel}
            </Box>
            {ledger?.archived && (
              <>
                <Chip label="Archived" color="default" size="small" sx={{ opacity: 0.85 }} />
                {isOwner && (
                  <Tooltip title="Restore this ledger to the active list">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Unarchive ledger"
                        onClick={handleUnarchive}
                        disabled={unarchiving}
                      >
                        <UnarchiveIcon size={20} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        );

        return (
          <PageHero
            back={{ label: 'Ledgers', onClick: () => navigate('/ledgers') }}
            label={label}
            aux={aux}
            sx={{
              position: 'sticky',
              // Track the bar's responsive minHeight exactly (48 xs / 52 sm+)
              // so no see-through sliver can open between bar and hero.
              top: '44px',
              zIndex: theme.zIndex.appBar - 1,
              backgroundColor: palette.surface.raised,
              marginBottom: `${ds.space[6]}px`,
            }}
          />
        );
      })()}

      {/* v4 mobile (#1377 board): date-grouped bare cards, 20px reconcile
          circles, quick-add via the bottom Sheet (convention 4). */}
      {useCardView && (
        <Box ref={tableContainerRef} sx={{ mt: '12px', pb: canEdit && !ledger?.archived ? '96px' : 0 }}>
          {(scrollLoading || initialLoading) && filteredLedgerLines.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <DollarSpinner size="large" message="Loading transactions..." />
            </Box>
          )}
          {filteredLedgerLines.length === 0 && !scrollLoading && !initialLoading && (
            <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
              {showUnreconciledOnly
                ? 'No unreconciled transactions found.'
                : 'No transactions yet - add your first one below'}
            </Typography>
          )}
          {mobileGroups.map((g) => (
            <Box key={g.key} data-pinned-line={g.pinned ? g.rows[0].line.lineId : undefined} sx={{ mb: '13px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px 6px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Box
                    sx={{
                      width: ds.size.dot,
                      height: ds.size.dot,
                      borderRadius: '50%',
                      backgroundColor: iconBoxColors(theme, ledgerTypeCategory(ledger?.type)).accent,
                    }}
                  />
                  <Box sx={{ fontSize: '12px', fontWeight: ds.font.weightMedium, color: palette.text.secondary }}>
                    {formatDate(g.date, 'date-only')}
                  </Box>
                </Box>
                <Box sx={{ fontSize: '12px', color: palette.text.dim, fontVariantNumeric: 'tabular-nums' }}>
                  <MaskedCurrency value={g.balance} />
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: palette.surface.raised,
                  border: `1px solid ${palette.divider}`,
                  borderRadius: `${ds.radius.container}px`,
                  padding: '1px 14px',
                }}
              >
                {g.rows.map(({ line, index }) => {
                  const isLastOverall = index === sentinelIndex;
                  if (editingLine !== line.lineId) {
                    // Memoized card (#1790): stable props only, so typing in the
                    // Sheet or the editing card leaves these cards untouched.
                    return (
                      <LedgerLineCard
                        key={line.lineId}
                        ref={isLastOverall ? lastElementRef : null}
                        line={line}
                        isUpdatingClear={updatingClear.has(line.lineId)}
                        canEdit={canEdit}
                        showMenu={canEdit && !ledger?.archived}
                        menuDisabled={editingLine !== null}
                        moveDisabled={ledgers.length === 0}
                        onClearToggle={handleClearToggle}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                        onMove={handleMoveClick}
                      />
                    );
                  }
                  // Editing card: one row, re-renders with the page by design.
                  return (
                    <Box
                      key={line.lineId}
                      ref={isLastOverall ? lastElementRef : null}
                      data-line-id={line.lineId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '11px',
                        padding: '9px 0',
                        borderBottom: `1px solid ${palette.divider}`,
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', py: '4px' }}>
                        <TextField
                          type="date"
                          inputProps={{ min: DATE_STAMP_MIN, max: DATE_STAMP_MAX }}
                          size="small"
                          value={editData.dateStamp || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, dateStamp: e.target.value }))}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PayeeAutocomplete
                          size="small"
                          sx={{ flex: 1, minWidth: 0 }}
                          onOpen={editPrompt.close}
                          prompt={editPrompt}
                          incomeAllowed={editPrompt.incomeAllowed}
                          options={filteredPayees}
                          getOptionLabel={(o) => o.name || ''}
                          value={payeeOptionValue(payees, editData.payeeId, editPrompt.paycheck)}
                          onChange={handlePayeeChange}
                          inputValue={payeeInputValue}
                          onInputChange={handlePayeeInputChange}
                          freeSolo
                          slotProps={SUGGESTION_POPPER_SLOTPROPS}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Type payee name..."
                              onBlur={(e) => editPrompt.onPayeeBlur(payeeInputValue, e)}
                            />
                          )}
                        />
                        </Box>
                        {/* The Entry Type chip leads the Memo field (#1938); the
                            control stacks under it. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <EntryTypeTrigger
                            ref={setCardChipAnchor}
                            type={editPrompt.chip?.type}
                            differs={editPrompt.chip?.differs}
                            readOnly={Boolean(editPrompt.chip?.locked)}
                            open={editPrompt.open}
                            onToggle={editPrompt.toggle}
                          />
                          <TextField
                            size="small"
                            placeholder="Memo (optional)"
                            value={editData.memo || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, memo: sanitizeMemo(e.target.value) }))}
                            inputProps={{ maxLength: MEMO_MAX_LENGTH }}
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                        </Box>
                        <EntryTypeControl
                          placement="inline"
                          open={editPrompt.open}
                          anchorEl={cardChipAnchor}
                          value={editPrompt.value}
                          onChange={editPrompt.setValue}
                          onClose={editPrompt.close}
                          prompt={editPrompt.entry}
                          pending={editPrompt.pending}
                          onProviderChoice={editPrompt.setProviderChoice}
                          incomeAllowed={editPrompt.incomeAllowed}
                        />
                        <AmountInputCell
                          fullRow
                          debitValue={editData.debit}
                          creditValue={editData.credit}
                          onDebitChange={(val) => setEditData(prev => ({ ...prev, debit: val, credit: val ? '' : prev.credit }))}
                          onCreditChange={(val) => setEditData(prev => ({ ...prev, credit: val, debit: val ? '' : prev.debit }))}
                          depositOnly={editPrompt.paycheck}
                          depositOnlyTitle={engineStrings['entryType.depositOnly']}
                        />
                        <EnhancedBudgetAutocomplete
                          budgets={budgets}
                          value={budgets.find(b => b.budgetId === editData.budgetId) || null}
                          onChange={(event, newValue) => setEditData(prev => ({ ...prev, budgetId: newValue ? newValue.budgetId : '0' }))}
                        />
                        <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <Button size="small" onClick={handleEditCancel} disabled={saving}>Cancel</Button>
                          <Button size="small" variant="contained" data-entry-type-save="true" onClick={() => handleEditSave(line)} disabled={saving || editPrompt.pending}>
                            {saving ? 'Saving...' : 'Save'}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <DollarSpinner size="medium" message="Loading more transactions..." />
            </Box>
          )}
          {autoLoadPaused && !loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<IconChevronDown size={16} />}
                onClick={() => loadMore()}
              >
                Load older transactions
              </Button>
            </Box>
          )}
          {!hasMore && ledgerLines.length > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
              No more transactions to load
            </Typography>
          )}
        </Box>
      )}

      {/* Ledger Lines Table - v4 flush-table surface box (sm+; no section header) */}
      {!useCardView && (
      <Box
        sx={{
          backgroundColor: palette.surface.raised,
          border: `1px solid ${palette.divider}`,
          borderRadius: `${ds.radius.container}px`,
          padding: '0 16px',
          marginTop: { xs: '12px', md: '14px' },
        }}
      >
          {/* Always show the table with NewLedgerRow for data entry */}
          <TableContainer ref={tableContainerRef}>
              <Table 
                size="small" 
                sx={{ 
                  tableLayout: 'auto',
                  '& .MuiTableCell-root': { padding: '6px 8px' },
                  // Responsive table layout
                  minWidth: 650,
                  '@media (max-width: 900px)': {
                    minWidth: 'unset'
                  },
                  '@media (min-width: 600px) and (max-width: 900px)': {
                    '& .MuiTableCell-root': {
                      padding: '4px 6px',
                      fontSize: '0.875rem'
                    }
                  }
                }}
              >
                {/* v4 board table header (#1377): 11px uppercase dim cells;
                    columns 36/90/1fr/132/140/160/130/28 on desktop (#1549 added Memo;
                    #1938 widened it for the Entry Type chip). */}
                <TableHead sx={{ display: { xs: 'none', sm: 'table-header-group' } }}>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-root': {
                        fontSize: '11px',
                        fontWeight: ds.font.weightMedium,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: palette.text.dim,
                        padding: '8px 8px 9px',
                      },
                    }}
                  >
                    <TableCell sx={{ width: { sm: '36px' } }} />
                    <TableCell align="left" sx={{ width: { sm: '76px', md: '90px' } }}>Date</TableCell>
                    <TableCell align="left" sx={{ minWidth: { sm: '120px' }, width: 'auto' }}>Payee</TableCell>
                    <TableCell align="left" sx={{ width: { sm: '106px', md: '132px' } }}>Memo</TableCell>
                    <TableCell align="right" sx={{ width: { sm: '110px', md: '140px' } }}>Amount</TableCell>
                    <TableCell align="left" sx={{ width: { sm: '110px', md: '160px' } }}>Budget</TableCell>
                    <TableCell align="right" sx={{ width: { sm: '110px', md: '130px' } }}>Balance</TableCell>
                    <TableCell align="center" sx={{ width: '28px', padding: '0 4px' }}>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* New row for adding transactions - editor/owner only; hidden when archived */}
                  {canEdit && !ledger?.archived && <NewLedgerRow
                    newRowData={newRowData}
                    setNewRowData={setNewRowData}
                    newRowPayeeInput={newRowPayeeInput}
                    handleNewRowPayeeInputChange={handleNewRowPayeeInputChange}
                    newRowBudgetInput={newRowBudgetInput}
                    handleNewRowBudgetInputChange={handleNewRowBudgetInputChange}
                    handleNewRowPayeeChange={handleNewRowPayeeChange}
                    filteredPayees={filteredNewRowPayees}
                    payees={payees}
                    budgets={budgets}
                    handleSaveNewRow={handleSaveNewRow}
                    savingNewRow={savingNewRow}
                    handleCreatePayeeFromNewRow={handleCreatePayeeFromNewRow}
                    showNewRowPayeeTooltip={showNewRowPayeeTooltip}
                    entryPrompt={newRowPromptView}
                    dateFieldRef={dateFieldRef}
                    payeeFieldRef={payeeFieldRef}
                    debitFieldRef={debitFieldRef}
                    creditFieldRef={creditFieldRef}
                    budgetFieldRef={budgetFieldRef}
                    saveButtonRef={saveButtonRef}
                    splitOpen={splitOpen}
                    onSplitToggle={handleSplitToggle}
                  />}
                  {canEdit && !ledger?.archived && splitOpen && (
                    <SplitPartRows
                      parts={splitParts}
                      lastCents={lastSplitCents}
                      budgets={budgets}
                      onPartChange={handleSplitPartChange}
                    />
                  )}

                  {/* Existing transactions */}
                  {/* Edit-only props reach the editing row alone (#1790). */}
                  {/* The pinned line, if any, renders first (#1933). */}
                  {renderOrder.map((index) => {
                    const line = filteredLedgerLines[index];
                    const isLast = index === sentinelIndex;
                    const isEditing = editingLine === line.lineId;
                    return (
                      <LedgerTableRow
                        key={line.lineId}
                        ref={isLast ? lastElementRef : null}
                        line={line}
                        runningBalance={runningBalances[index]}
                        isEditing={isEditing}
                        isUpdatingClear={updatingClear.has(line.lineId)}
                        handleClearToggle={handleClearToggle}
                        handleEditClick={handleEditClick}
                        handleDeleteClick={handleDeleteClick}
                        handleMoveClick={handleMoveClick}
                        canMoveLedger={ledgers.length > 0}
                        formatDate={formatDate}
                        canEdit={canEdit && !ledger?.archived}
                        isHighlighted={highlightedLineId === line.lineId}
                        {...(isEditing ? editRowProps : null)}
                      />
                    );
                  })}

                  {/* Initial transaction load - spinner inside the table (matches BudgetDetail).
                      Covers the line fetch whether driven by scrollLoading or the
                      page-level initialLoading, so the big centered loader is not shown. */}
                  {(scrollLoading || initialLoading) && filteredLedgerLines.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <DollarSpinner size="large" message="Loading transactions..." />
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Empty state message for ledgers with no transactions */}
                  {filteredLedgerLines.length === 0 && !scrollLoading && !initialLoading && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography sx={{ color: 'text.secondary' }}>
                          {showUnreconciledOnly
                            ? 'No unreconciled transactions found.'
                            : 'No transactions yet - add your first one above'
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Infinite scroll loading indicator */}
                  {loadingMore && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <DollarSpinner size="medium" message="Loading more transactions..." />
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Auto-load cap reached (#1790): one more page per tap */}
                  {autoLoadPaused && !loadingMore && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<IconChevronDown size={16} />}
                          onClick={() => loadMore()}
                        >
                          Load older transactions
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* End of data indicator */}
                  {!hasMore && ledgerLines.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No more transactions to load
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
      </Box>
      )}

      {/* v4 mobile quick-add (convention 4): pinned ActionBar + bottom Sheet */}
      {useCardView && canEdit && !ledger?.archived && (
        <>
          <ActionBar hint="tap the circle to reconcile">
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<IconPlus size={16} />}
              onClick={() => setAddSheetOpen(true)}
              disabled={editingLine !== null}
            >
              Add transaction
            </Button>
          </ActionBar>
          <Sheet open={addSheetOpen} onClose={() => setAddSheetOpen(false)} title="Add transaction">
            <Box ref={sheetContentRef} sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <TextField
                label="Date"
                type="date"
                inputProps={{ min: DATE_STAMP_MIN, max: DATE_STAMP_MAX }}
                size="small"
                value={newRowData.dateStamp || ''}
                onChange={(e) => setNewRowData(prev => ({ ...prev, dateStamp: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PayeeAutocomplete
                size="small"
                sx={{ flex: 1, minWidth: 0 }}
                onOpen={newRowPrompt.close}
                prompt={newRowPrompt}
                incomeAllowed={newRowPrompt.incomeAllowed}
                options={filteredNewRowPayees}
                getOptionLabel={(o) => o.name || ''}
                value={payeeOptionValue(payees, newRowData.payeeId, newRowPrompt.paycheck)}
                onChange={handleNewRowPayeeChange}
                inputValue={newRowPayeeInput}
                onInputChange={handleNewRowPayeeInputChange}
                freeSolo
                slotProps={SUGGESTION_POPPER_SLOTPROPS}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Payee"
                    placeholder="Type payee name..."
                    onBlur={(e) => newRowPrompt.onPayeeBlur(newRowPayeeInput, e)}
                  />
                )}
              />
              </Box>
              {/* The Entry Type chip leads the Memo field (#1938); the control
                  stacks under it. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <EntryTypeTrigger
                  ref={setSheetChipAnchor}
                  type={newRowPrompt.chip?.type}
                  differs={newRowPrompt.chip?.differs}
                  readOnly={Boolean(newRowPrompt.chip?.locked)}
                  open={newRowPrompt.open}
                  onToggle={newRowPrompt.toggle}
                />
                <TextField
                  label="Memo (optional)"
                  size="small"
                  value={newRowData.memo || ''}
                  onChange={(e) => setNewRowData(prev => ({ ...prev, memo: sanitizeMemo(e.target.value) }))}
                  inputProps={{ maxLength: MEMO_MAX_LENGTH }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Box>
              <EntryTypeControl
                placement="inline"
                open={newRowPrompt.open}
                anchorEl={sheetChipAnchor}
                value={newRowPrompt.value}
                onChange={newRowPrompt.setValue}
                onClose={newRowPrompt.close}
                prompt={newRowPrompt.entry}
                pending={newRowPrompt.pending}
                onProviderChoice={newRowPrompt.setProviderChoice}
                incomeAllowed={newRowPrompt.incomeAllowed}
              />
              <AmountInputCell
                fullRow
                debitValue={newRowData.debit}
                creditValue={newRowData.credit}
                onDebitChange={(val) => setNewRowData(prev => ({ ...prev, debit: val, credit: val ? '' : prev.credit }))}
                onCreditChange={(val) => setNewRowData(prev => ({ ...prev, credit: val, debit: val ? '' : prev.debit }))}
                depositOnly={newRowPrompt.paycheck}
                depositOnlyTitle={engineStrings['entryType.depositOnly']}
              />
              <EnhancedBudgetAutocomplete
                budgets={budgets}
                value={budgets.find(b => b.budgetId === newRowData.budgetId) || null}
                onChange={(event, newValue) => setNewRowData(prev => ({ ...prev, budgetId: newValue ? newValue.budgetId : '0' }))}
                inputValue={newRowBudgetInput}
                onInputChange={handleNewRowBudgetInputChange}
              />
              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={savingNewRow || newRowPrompt.pending}
                data-entry-type-save="true"
                onClick={async () => {
                  const errors = collectQuickAddErrors();
                  if (errors.length > 0) {
                    // Measure the open sheet so the toasts float just above
                    // its top edge; zeroed rects (jsdom) fall back to 96px.
                    const paper = sheetContentRef.current?.closest('.MuiDrawer-paper');
                    const top = paper?.getBoundingClientRect().top;
                    const bottomOffset =
                      Number.isFinite(top) && top > 0 ? Math.round(window.innerHeight - top) + 8 : 96;
                    errors.forEach((msg) => showError(msg, { anchor: 'bottom', bottomOffset }));
                    return;
                  }
                  const ok = await handleSaveNewRow();
                  if (ok) setAddSheetOpen(false);
                }}
              >
                {savingNewRow ? 'Saving...' : 'Save transaction'}
              </Button>
            </Box>
          </Sheet>
        </>
      )}

      {/* Move-to-ledger dialog (#760) */}
      <Dialog open={Boolean(moveLine)} onClose={handleMoveCancel} fullWidth maxWidth="xs">
        <DialogTitle>Move line to another ledger</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            The line keeps its date, payee, amount, and budget. Both ledger balances update.
          </Typography>
          <Autocomplete
            options={ledgers}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option.ledgerId === value.ledgerId}
            value={moveTargetLedger}
            onChange={(event, newValue) => setMoveTargetLedger(newValue)}
            disabled={moving}
            renderInput={(params) => (
              <TextField {...params} label="Destination ledger" autoFocus />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMoveCancel} disabled={moving}>Cancel</Button>
          <Button
            onClick={handleMoveConfirm}
            variant="contained"
            disabled={moving || !moveTargetLedger}
          >
            {moving ? 'Moving...' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Line Confirmation Dialog (#1452) */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete transaction?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this ledger line?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LedgerDetail;